import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import toast from "react-hot-toast";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import MoneyInput from "../money-input";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faMinusSquare, faPlus, faTrash} from "@fortawesome/free-solid-svg-icons";
import Utils from "../../utils/utils";
import {fetch} from "../../utils/api-wrapper";
import {loadXpensCategories, getXpensCategoryLabel} from "../../utils/xpens-categories";
import {imputationDocumentId, mongoExtendedJsonId} from "../../pages/review-expense/utils/contract-expense-json";
import {cachedFetch} from "../../utils/cached-api-wrapper";
import FormTextfield from "../form-textfield";
import AutoCompleteField from "../field-auto-complete";
import "./workflow-edit-step-modal.scss";
import { IMPUTATION_CATEGORY_SORTING, IMPUTATION_FETCH_PAGE_SIZE, imputationRowHasParentId } from "../../utils/expenses-imputations-config-helpers";
import { shouldUseInvoiceOfficeLabel } from "../../utils/additional-fields-filter-helpers";
import { isNegatingUserOperator } from "../../utils/workflow-condition-operators";
import { glAccountClassLabel, glAccountClassNode } from "../../utils/gl-account-class";

import InvoiceNumberTemplate from "./invoice-number-template";
import {DEFAULT_INVOICE_NUMBER_TEMPLATE} from "../../utils/workflow-invoice-number";

// XPEN-3206 and XPEN-3414: two conditions read a field off the line's GL account.
// Both take one or more values -- the engine splits the stored value on commas --
// and both are offered only on Elite, where the fields are synced.
const GL_ACCOUNT_CONDITION_FIELDS = {
    item_gl_class: {field: "GLAcctClass", name: "gl account class"},
    item_gl_category: {field: "GLAcctCategoryList", name: "gl account category"},
};

const GL_SECTION_WORKFLOW_VARIABLE = {
    type: "workflow_variable",
    value: "first_4_digits_of_gl_natural",
};

const isGLSectionWorkflowVariable = (value) =>
    value?.type === GL_SECTION_WORKFLOW_VARIABLE.type &&
    value?.value === GL_SECTION_WORKFLOW_VARIABLE.value;

const REQUEST_INVOICE_APPROVAL = "request_invoice_approval";
const DEFAULT_INVOICE_APPROVAL_VALUE = {
    approver_source: "manual_user",
    user_id: "",
};
const REQUEST_INVOICE_ANY_OF_APPROVAL = "request_invoice_any_of_approval";
const DEFAULT_ANY_OF_SPEC = {
    approver_source: "manual_user",
    user_id: "",
};
const REQUEST_LINE_ITEM_APPROVAL = "request_line_item_approval";
// XPEN-3414: one action, and the level it applies at is one of its three choices.
const REQUEST_SECTION_APPROVAL = "request_section_approval";
const DEFAULT_SECTION_APPROVAL_VALUE = {target: "invoice", section: "", role: "Approver"};
const DEFAULT_LINE_ITEM_APPROVAL_VALUE = {
    approver_source: "manual_user",
    user_id: "",
};
const GL_BETWEEN_OPERATOR = "between";
const TAX_CODE_CONDITION_TYPES = ["tax_code", "item_tax_code"];
const BILLABLE_CONDITION_TYPES = ["invoice_billable", "item_billable"];
const RENAME_INVOICE_FILE = "rename_invoice_file";
const DEFAULT_RENAME_FILE_VALUE = {
    pieces: ["supplier_name", "invoice_date", "invoice_number"],
    separator: "_",
};

const normalizeGlBetweenValue = (value) => {
    if (typeof value === "object" && value !== null) {
        return {
            min: typeof value.min === "string" ? value.min : "",
            max: typeof value.max === "string" ? value.max : "",
        };
    }
    return {min: "", max: ""};
};

const normalizeInvoiceApprovalAction = (action) => {
    if (action?.type === "request_approval") {
        return {
            ...action,
            type: REQUEST_INVOICE_APPROVAL,
            value: {
                approver_source: "manual_user",
                user_id: action.value || "",
            },
        };
    }

    if (action?.type === "request_invoice_user_approval") {
        return {
            ...action,
            type: REQUEST_INVOICE_APPROVAL,
            value: {approver_source: "invoice_user"},
        };
    }

    if (action?.type === "request_gl_responsible_approval") {
        return {
            ...action,
            type: REQUEST_INVOICE_APPROVAL,
            value: {approver_source: "gl_responsible"},
        };
    }

    if (action?.type === "request_matter_responsible_approval") {
        return {
            ...action,
            type: REQUEST_INVOICE_APPROVAL,
            value: {approver_source: "matter_responsible"},
        };
    }

    if (action?.type === "request_department_approval") {
        const value = typeof action.value === "object" && action.value !== null ? action.value : {};
        return {
            ...action,
            type: REQUEST_INVOICE_APPROVAL,
            value: {
                approver_source: "department",
                department: value.department || "",
                function: value.function || "",
            },
        };
    }

    return action;
};

const normalizeLineItemApprovalAction = (action) => {
    if (action?.type === REQUEST_LINE_ITEM_APPROVAL) {
        if (typeof action.value === "object" && action.value !== null && action.value.approver_source) {
            return action;
        }
        return {
            ...action,
            value: {
                approver_source: "manual_user",
                user_id: action.value || "",
            },
        };
    }

    if (action?.type === "request_line_item_gl_responsible_approval") {
        return {
            ...action,
            type: REQUEST_LINE_ITEM_APPROVAL,
            value: {approver_source: "gl_responsible"},
        };
    }

    if (action?.type === "request_line_item_matter_responsible_approval") {
        return {
            ...action,
            type: REQUEST_LINE_ITEM_APPROVAL,
            value: {approver_source: "matter_responsible"},
        };
    }

    if (action?.type === "request_line_item_category_responsible_approval") {
        return {
            ...action,
            type: REQUEST_LINE_ITEM_APPROVAL,
            value: {approver_source: "category_responsible"},
        };
    }

    if (action?.type === "request_line_item_subcategory_responsible_approval") {
        return {
            ...action,
            type: REQUEST_LINE_ITEM_APPROVAL,
            value: {approver_source: "subcategory_responsible"},
        };
    }

    if (action?.type === "request_line_item_department_approval") {
        const value = typeof action.value === "object" && action.value !== null ? action.value : {};
        return {
            ...action,
            type: REQUEST_LINE_ITEM_APPROVAL,
            value: {
                approver_source: "department",
                department: value.department || "",
                function: value.function || "",
            },
        };
    }

    return action;
};

const PRIMARY_SIDE = "primary";
const ALTERNATE_SIDE = "alternate";
// A side is a condition set plus the actions it triggers. The alternate side is an
// "otherwise if": it has conditions of its own, so both sections follow the toggle.
const SIDE_KEYS = {
    [PRIMARY_SIDE]: { conditions: "conditions", logic: "condition_logic", actions: "actions" },
    [ALTERNATE_SIDE]: {
        conditions: "alternate_conditions",
        logic: "alternate_condition_logic",
        actions: "alternate_actions",
    },
};

const normalizeWorkflowStep = (step) => ({
    ...step,
    conditions: step?.conditions || [],
    actions: (step?.actions || [])
        .map(normalizeInvoiceApprovalAction)
        .map(normalizeLineItemApprovalAction),
    // The "otherwise if" side: its own conditions, and the actions they trigger.
    alternate_conditions: step?.alternate_conditions || [],
    alternate_condition_logic: step?.alternate_condition_logic || "all",
    alternate_actions: (step?.alternate_actions || [])
        .map(normalizeInvoiceApprovalAction)
        .map(normalizeLineItemApprovalAction),
});

const WorkflowEditStepModal = ({ selectedStep, setSelectedStep, data, onSubmit, onCancel,
                                 conditionsOnly = false }) => {
    // Local copy of the step being edited. Changes are applied locally until submission.
    const [localStep, setLocalStep] = useState(() => normalizeWorkflowStep(selectedStep));
    // One toggle drives both sections, so each side reuses the same editors rather
    // than a second copy kept in step by hand.
    const [side, setSide] = useState(PRIMARY_SIDE);
    const conditionsKey = SIDE_KEYS[side].conditions;
    const logicKey = SIDE_KEYS[side].logic;
    const actionsKey = SIDE_KEYS[side].actions;
    // Reference for generating unique IDs for split item lines.
    const valId = useRef(0);
    const [approvers, setApprovers] = useState([]);
    const [clientImputations, setClientImputations] = useState([]);
    const [expenseRates, setExpenseRates] = useState({});
    const [taxCodes, setTaxCodes] = useState([]);
    const [departments, setDepartments] = useState([]);
    // XPEN-3586: the client's own roles, custom ones included. `config.roles` only
    // holds the defaults, so a firm could not pick the roles it actually uses.
    // `/roles/names/` needs a session only -- the `<client_id>` variant requires
    // user-admin rights a workflow editor need not have.
    const [clientRoleNames, setClientRoleNames] = useState([]);
    useEffect(() => {
        fetch("/roles/names/", "GET", {}, (response) => {
            const names = Array.isArray(response?.data) ? response.data.filter(Boolean) : [];
            setClientRoleNames(names);
        });
    }, []);
    // field_name -> [{value, label, description}], for the Elite-only pickers.
    const [codeOptions, setCodeOptions] = useState({});
    const functions = [
        {name: "ceo"},
        {name: "cto"},
        {name: "lawyer"},
        {name: "employee"},
        {name: "cfo"},
        {name: "accountant"},
        {name: "coo"},
        {name: "director_of_finance"},
        {name: "director_of_technology"},
        {name: "managing_partner"},
    ]

    const invoiceApprovalSourceOptions = [
        { value: "manual_user", label: data.t.read("rule.request_approval_from_user") },
        { value: "invoice_user", label: data.t.read("rule.request_approval_from_invoice_user") },
        { value: "gl_responsible", label: data.t.read("rule.request_approval_from_gl_responsible") },
        { value: "matter_responsible", label: data.t.read("rule.request_approval_from_matter_responsible") },
        { value: "matter_practice_group_timekeeper", label: data.t.read("rule.request_approval_from_matter_practice_group_timekeeper") },
        { value: "department", label: data.t.read("rule.request_department_approval") },
    ];
    const lineItemApprovalSourceOptions = [
        { value: "manual_user", label: data.t.read("rule.request_line_item_approval") },
        { value: "gl_responsible", label: data.t.read("rule.request_line_item_approval_from_gl_responsible") },
        { value: "matter_responsible", label: data.t.read("rule.request_line_item_approval_from_matter_responsible") },
        { value: "matter_practice_group_timekeeper", label: data.t.read("rule.request_line_item_approval_from_matter_practice_group_timekeeper") },
        { value: "department", label: data.t.read("rule.request_line_item_department_approval") },
        { value: "category_responsible", label: data.t.read("rule.request_line_item_approval_from_category_responsible") },
        { value: "subcategory_responsible", label: data.t.read("rule.request_line_item_approval_from_subcategory_responsible") },
        { value: "subcategory_approvers", label: data.t.read("rule.request_line_item_approval_from_subcategory_approvers") },
    ];

    const itemDetailFieldOptions = [
        { value: "GLNatural", label: data.t.read("gl_natural") },
        { value: "GLUnit", label: data.t.read("invoice.unit") },
        { value: "Office", label: data.t.read("office") },
        { value: "GLDepartement", label: data.t.read("invoice.gl_departement") },
        { value: "GLSection", label: data.t.read("invoice.gl_section") },
        { value: "Currency", label: data.t.read("invoice.currency") },
        { value: "InvoiceRef", label: data.t.read("invoice.invoice_ref") },
        { value: "CostType", label: data.t.read("cost_type") },
        { value: "Description", label: data.t.read("description") },

    ];

    // GL Office is a separate Elite line-item segment; it must not alter invoice-level Office actions.
    const lineItemDetailFieldOptions = itemDetailFieldOptions.flatMap(option =>
        option.value === "Office" && shouldUseInvoiceOfficeLabel(data)
            ? [option, { value: "GLOffice", label: data.t.read("gl_office") }]
            : [option]
    );

    // XPEN-3187: safe no-op if already cached by App.js's session-level fetch.
    useEffect(() => {
        loadXpensCategories(data);
    }, [data]);

    const categoryLabel = (category) => getXpensCategoryLabel(data, category);

    const xpensCategoryOptions = (data.xpensCategories || [])
        .map((cat) => ({
            value: mongoExtendedJsonId(cat._id),
            label: getXpensCategoryLabel(data, cat._id) || cat.name_en || cat.code,
        }))
        .filter((option) => option.value && option.label);

    const clientCategoryOptions = clientImputations
        .filter((row) => !imputationRowHasParentId(row))
        .map((row) => ({
            value: imputationDocumentId(row),
            label: row.category || data.t.read("n_a"),
        }))
        .filter((option) => option.value);

    const selectedClientCategoryOption = (stored) => {
        if (!stored) return null;
        const needle = (mongoExtendedJsonId(stored) || "").toLowerCase();
        const match = needle
            ? clientCategoryOptions.find((option) => (option.value || "").toLowerCase() === needle)
            : null;
        if (match) return match;
        const fallback = mongoExtendedJsonId(stored);
        return { value: fallback || stored, label: fallback || String(stored) };
    };

    const selectedXpensCategoryOption = (stored) => {
        if (!stored) return null;
        const needle = (mongoExtendedJsonId(stored) || "").toLowerCase();
        const match = needle
            ? xpensCategoryOptions.find((option) => (option.value || "").toLowerCase() === needle)
            : null;
        return match || { value: mongoExtendedJsonId(stored) || stored, label: categoryLabel(stored) };
    };

    const functionMap = fonction => ({
        value: fonction.name,
        label: data.t.read(fonction.name),
    });

    const sanitizeSearchQuery = (value = "") => value.trim().replace(/\//g, "");

    // The chip for a selected user. Falls back the same way userMap's label does,
    // so a user with no name still shows something recognisable.
    const userChipLabel = (option) => {
        const user = option?.value || {};
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        return fullName || user.username || user.email || option?.label || "";
    };

    const userMap = (user) => {
        if (typeof user !== "object" || user === null) {
            return null;
        }

        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        const labelBase = fullName || user.username || user.email || "(user)";
        const label = user.email ? `${labelBase} (${user.email})` : labelBase;

        return {
            value: {
                id_user: user.id_user,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
                role: user.role,
            },
            label,
        };
    };

    const departmentSearchMap = (department) => {
        if (typeof department !== "object" || department === null) {
            return null;
        }

        const id = department._id?.$oid || department.id || department._id;
        const label = department.name || department.label || "";

        return {
            value: { id, name: label },
            label,
        };
    };

    // Whenever localStep changes, update the parent selectedStep.
    useEffect(() => {
        // The whole alternate side is dropped when it is empty: these keys exist only
        // on steps that use them, so editing any other step does not start carrying
        // them.
        const {
            alternate_conditions: alternateConditions,
            alternate_condition_logic: alternateLogic,
            alternate_actions: alternateActions,
            ...rest
        } = localStep;
        const hasAlternate = (alternateActions || []).length > 0
            || (alternateConditions || []).length > 0;
        setSelectedStep(hasAlternate
            ? {
                ...rest,
                alternate_conditions: alternateConditions || [],
                alternate_condition_logic: alternateLogic || "all",
                alternate_actions: alternateActions || [],
            }
            : { ...rest });
        refreshTaxCodes();
        fetchDepartments();
        fetchClientImputations();
    }, [localStep, setSelectedStep]);

    useEffect(() => {
        fetchExpenseRates();
        fetchCodeOptions();
    }, []);


    const fetchDepartments = () => {
        const filters = [{id: "id_client", value: data.id_client}];
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify([]))}&start=${encodeURIComponent(0)}&page_size=${100}`;
        fetch(`/departments/${searchData}`, "GET", {},
            response => {
                let fetchedDepartments = response.data.departments;
                setDepartments([...fetchedDepartments]);
            });
    }

    const fetchClientImputations = () => {
        // Displacement imputations must not appear in workflow step cost-type pickers — scope to expense catalog only.
        // Paginated loop so large catalogs are not silently truncated at the API default page size.
        const filters = [{ id: "imputation_catalog", value: "expense" }];
        const all = [];
        const fetchPage = (start) => {
            const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify(IMPUTATION_CATEGORY_SORTING))}&start=${start}&page_size=${IMPUTATION_FETCH_PAGE_SIZE}`;
            fetch(`/imputations/${searchData}`, "GET", {}, response => {
                const rows = response?.data?.imputations ?? [];
                const totalRowCount = response?.data?.meta?.totalRowCount ?? 0;
                all.push(...rows);
                if (rows.length > 0 && all.length < totalRowCount) {
                    fetchPage(start + rows.length);
                } else {
                    setClientImputations(all);
                }
            }, () => {
                toast.error(data.t.read("failed_to_fetch"));
            });
        };
        fetchPage(0);
    }
    // Served already sorted by /client/options/ (live from 3E, or the codes the
    // sync wrote into our own collections). Elite-only, like the conditions and
    // the action that use them, so no other exporter pays the requests.
    const CODE_OPTION_FIELDS = ["GLAcctClass", "GLAcctCategoryList", "Section"];

    const fetchCodeOptions = () => {
        if (!Utils.isExportFormat(data, 'elite')) return;
        CODE_OPTION_FIELDS.forEach(fieldName => {
            cachedFetch(`/client/options/?field_name=${fieldName}&filters=${encodeURIComponent("[]")}`,
                "GET", {},
                response => {
                    const rows = Array.isArray(response?.data) ? response.data : [];
                    setCodeOptions(current => ({
                        ...current,
                        [fieldName]: rows
                            // The code is trimmed before it becomes the stored value,
                            // not only before it is displayed: a padded one would be
                            // saved as the rule's value and then never match again.
                            .map(row => ({value: String(row?.Id ?? "").trim(),
                                          description: row?.Description}))
                            .filter(option => option.value)
                            .map(option => ({
                                ...option,
                                // Plain text, so react-select's own search still
                                // matches on both halves; `description` is what the
                                // render picks apart.
                                label: glAccountClassLabel(option.value, option.description),
                            })),
                    }));
                },
                // Without this an unreachable endpoint is indistinguishable from a
                // firm that has none at all -- both render an empty picker.
                () => toast.error(data.t.read("failed_to_fetch")));
        });
    }

    const fetchExpenseRates = () => {
        fetch("/expense_rates/", "GET", {}, response => {
            setExpenseRates(response.data?.rates || {});
        });
    }
    const refreshTaxCodes = () => {
        let client_id = data.id_client;
        fetch("/clients/" + client_id + "/tax-profile", "GET", {}, response => {
                const tax_profile = response.data.tax_profile;
                const codes = tax_profile.map(item => item.code);
                tax_profile.forEach((item, i) => {
                    item._view_id = i
                    item.list_regex_must_contain = [
                        (item.list_regex_must_contain?.[0]) ? item.list_regex_must_contain[0] : [],
                        (item.list_regex_must_contain?.[1]) ? item.list_regex_must_contain[1] : [],
                    ];
                })
                setTaxCodes(codes);
            },
        );
        
    }
  
    const departmentMap = (department) =>{
        return {
            value: department._id?.$oid,
            label: department.name,
        }
    }

    const updateCondition = (property, condition, event) => {
        if (!localStep) return;
        const nextValue = event.target.value;
        const updatedStep = {
            ...localStep,
            [conditionsKey]: localStep[conditionsKey].map(c =>
                c.id === condition.id ? (() => {
                    if (property === "type") {
                        return {
                            ...c,
                            type: nextValue,
                            // Billable conditions must persist the same Yes value shown by default.
                            value: BILLABLE_CONDITION_TYPES.includes(nextValue)
                                ? "1"
                                : nextValue === "user_is" || nextValue === "user_in_department" ? null : "",
                            operator: "equal",
                        };
                    }
                    if (property === "operator") {
                        const nextCondition = {...c, operator: nextValue};
                        if (c.type === "item_gl") {
                            nextCondition.value = nextValue === GL_BETWEEN_OPERATOR
                                ? normalizeGlBetweenValue(c.value)
                                : "";
                        }
                        return nextCondition;
                    }
                    return {...c, [property]: nextValue};
                })() : c,
            ),
        };
        setLocalStep(updatedStep);
    };


    const addCondition = () => {
        if (!selectedStep) return;
        const updatedStep = { ...localStep };
        const newConditionId = updatedStep[conditionsKey].length + 1;
        const newCondition = {
            id: newConditionId,
            type: "amount_greater",
            operator: "equal",
            value: "",
        };
        updatedStep[conditionsKey] = [...updatedStep[conditionsKey], newCondition];
        setLocalStep(updatedStep);
    };


    const removeCondition = (cond) => {
        const updatedStep = { ...localStep };
        updatedStep[conditionsKey] = updatedStep[conditionsKey].filter(c => c.id !== cond.id);
        // Reassign condition IDs sequentially.
        updatedStep[conditionsKey] = updatedStep[conditionsKey].map((c, idx) => ({ ...c, id: idx + 1 }));
        setLocalStep(updatedStep);
    };

    // -------------------- ACTION HANDLERS --------------------

    // Approvers state is used for approval request actions.


    const updateAction = (property, actionObj, event) => {
        if (!selectedStep) return;
        const updatedStep = { ...localStep };
        if (property === "addAction") {
            // Append a new action with default values.
            const newAction = {
                id: updatedStep[actionsKey].length + 1,
                type: "send_email",
                value: "",
            };
            updatedStep[actionsKey].push(newAction);
        } else {
            // Update the matching action.
            updatedStep[actionsKey] = updatedStep[actionsKey].map(a => {
                if (a.id === actionObj.id) {
                    // If updating the type, reset the value.
                    if (property === "type") {
                        let nextValue = "";
                        if (event.target.value === "split_mileage_by_secondary_rate") {
                            nextValue = {travel_type_id: "", secondary_rate: "", billable_cost_type: ""};
                        } else if (event.target.value === "split_tip_into_separate_item") {
                            nextValue = {cost_type: ""};
                        } else if (event.target.value === RENAME_INVOICE_FILE) {
                            nextValue = {...DEFAULT_RENAME_FILE_VALUE};
                        } else if (event.target.value === REQUEST_INVOICE_APPROVAL) {
                            nextValue = {...DEFAULT_INVOICE_APPROVAL_VALUE};
                        } else if (event.target.value === REQUEST_INVOICE_ANY_OF_APPROVAL) {
                            nextValue = [{...DEFAULT_ANY_OF_SPEC}];
                        } else if (event.target.value === REQUEST_LINE_ITEM_APPROVAL) {
                            nextValue = {...DEFAULT_LINE_ITEM_APPROVAL_VALUE};
                        } else if (event.target.value === REQUEST_SECTION_APPROVAL) {
                            nextValue = {...DEFAULT_SECTION_APPROVAL_VALUE};
                        } else if (event.target.value === "exclude_approver_roles") {
                            nextValue = [];
                        } else if (event.target.value === "set_invoice_number") {
                            nextValue = DEFAULT_INVOICE_NUMBER_TEMPLATE;
                        } else if (event.target.value === "sanitize_invoice_number_spaces") {
                            // Always a dash. The admin does not pick a replacement
                            // character. Empty string would mean "delete spaces" on
                            // the backend; persist "-" so Save and the engine match
                            // the seeded Aderant workflow.
                            nextValue = "-";
                        }
                        return {
                            ...a,
                            [property]: event.target.value,
                            value: nextValue,
                            day: "",
                            include_supplier_name: false,
                            include_invoice_number: false,
                        };
                    }
                    if (property === "field") {
                        return {
                            ...a,
                            field: event.target.value,
                            value: event.target.value === "GLSection" ? a.value : (isGLSectionWorkflowVariable(a.value) ? "" : a.value),
                        };
                    }
                    // For other properties, simply update.
                    return { ...a, [property]: event.target.value };
                }
                return a;
            });
        }
        setLocalStep(updatedStep);
    };


    const removeAction = (actionObj) => {
        if (!selectedStep) return;
        const updatedStep = { ...localStep };
        updatedStep[actionsKey] = updatedStep[actionsKey].filter(a => a.id !== actionObj.id);
        updatedStep[actionsKey] = updatedStep[actionsKey].map((a, idx) => ({ ...a, id: idx + 1 }));
        setLocalStep(updatedStep);
    };


    const addSplitItemValue = (actionObj) => {
        if (!selectedStep) return;
        const updatedStep = { ...localStep };
        updatedStep[actionsKey] = updatedStep[actionsKey].map(a => {
            if (a.id === actionObj.id) {
                if (!Array.isArray(a.value)) {
                    a.value = [];
                }
                a.value.push({
                    id: ++valId.current,
                    percent: 100,
                    updateInfo: "",
                    updateInfoValue: "",
                    updateCharacterAmount: null,
                    replaceAtTheStart: false,
                    maxAmount: "",
                });
            }
            return a;
        });
        setLocalStep(updatedStep);
    };


    const removeSplitItemValue = (actionObj, line) => {
        const updatedStep = { ...localStep };
        updatedStep[actionsKey] = updatedStep[actionsKey].map(a => {
            if (a.id === actionObj.id) {
                a.value = (a.value || []).filter(v => v !== line);
            }
            return a;
        });
        setLocalStep(updatedStep);
    };


    const updateSplitItemValue = (actionObj, line, property, e) => {
        if (!selectedStep) return;
        // Helper function: Convert string to boolean if applicable.
        const str2bool = (value) => {
            if (typeof value === "string") {
                if (value.toLowerCase() === "true") return true;
                if (value.toLowerCase() === "false") return false;
            }
            return value;
        };
        const updatedStep = { ...localStep };
        updatedStep[actionsKey] = updatedStep[actionsKey].map(a => {
            if (a.id === actionObj.id) {
                a.value = (a.value || []).map(v => {
                    if (v === line) {
                        return { ...v, [property]: str2bool(e.target.value) };
                    }
                    return v;
                });
            }
            return a;
        });
        setLocalStep(updatedStep);
    };

    // -------------------- OPTIONS FOR SELECT --------------------

    // Define the list of action types.
    const action_types = [
        { value: "send_email", label: data.t.read("rule.send_email_to") },
        { value: "split_items", label: data.t.read("rule.split_items") },
        { value: "split_mileage_by_secondary_rate", label: data.t.read("rule.split_mileage_by_secondary_rate") },
        { value: "split_tip_into_separate_item", label: data.t.read("rule.split_tip_into_separate_item") },
        { value: "only_keep_total", label: data.t.read("rule.only_keep_total") },
        { value: "change_currency", label: data.t.read("rule.change_currency_for") },
        { value: "change_all_totals", label: data.t.read("rule.change_all_totals") },
        { value: "change_user", label: data.t.read("rule.change_user") },
        { value: "change_item_description", label: data.t.read("rule.change_item_description") },
        { value: "change_item_detail", label: data.t.read("rule.change_item_detail") },
        { value: "change_invoice_default_field_value", label: data.t.read("rule.change_invoice_default_field_value") },
        { value: "change_status", label: data.t.read("rule.change_status") },
        { value: "achat_and_payment", label: `${data.t.read("activate")} ${data.t.read("achat_and_payment").toLowerCase()}`,},
        { value: "change_unit", label: data.t.read("rule.change_the_unit_to") },
        { value: "change_tax_codes", label: data.t.read("rule.change_the_tax_code_to") },
        { value: "change_item_tax_code", label: data.t.read("rule.change_item_tax_code") },
        { value: "change_cheque_reference", label: data.t.read("rule.change_cheque_reference") },
        { value: "set_invoice_number", label: data.t.read("rule.set_invoice_number") },
        { value: RENAME_INVOICE_FILE, label: data.t.read("rule.rename_invoice_file") },
        { value: "change_due_date", label: data.t.read("rule.change_due_date") },
        { value: "change_reference_to_matter_number", label: data.t.read("rule.change_reference_to_matter_number") },
        { value: "use_invoice_date_as_invoice_number", label: data.t.read("rule.use_invoice_date_as_invoice_number") },
        // Keep in sync with xpens-api RulesProcessor._sanitize_invoice_number_spaces.
        { value: "sanitize_invoice_number_spaces", label: data.t.read("rule.sanitize_invoice_number_spaces") },
        { value: "apply_gl_description_to_items", label: data.t.read("rule.apply_gl_description_to_items") },
        { value: "delete_line_item", label: data.t.read("rule.delete_line_item") },
        ...(Utils.userHasPageAccess(data, "approval")
            ? [
                { value: REQUEST_INVOICE_APPROVAL, label: data.t.read("rule.request_invoice_approval") },
                { value: REQUEST_INVOICE_ANY_OF_APPROVAL, label: data.t.read("rule.request_invoice_any_of_approval") },
                { value: REQUEST_LINE_ITEM_APPROVAL, label: data.t.read("rule.request_line_item_approval_group") },
                // A section comes off the Elite timekeeper, so the action is only
                // offered where there is one to pick.
                ...(Utils.isExportFormat(data, 'elite')
                    ? [{ value: REQUEST_SECTION_APPROVAL, label: data.t.read("rule.request_section_approval") }]
                    : []),
                { value: "delay_approval", label: data.t.read("rule.delay_approval") },
                { value: "append_approver_list", label: data.t.read("rule.add_to_approver_list") },
                { value: "exclude_approver_roles", label: data.t.read("rule.exclude_approver_roles") },
            ]
            : []),
        ...(Utils.userHasPageAccess(data, "expenses")
            ? [
                { value: "tax_deduction", label: data.t.read("rule.apply_tax_deduction") },
            ]
            : []),
    ];

    const renameFilePieceOptions = [
        { value: "", label: "--" },
        { value: "supplier_name", label: data.t.read("rule.filename_piece_supplier_name") },
        { value: "invoice_date", label: data.t.read("rule.filename_piece_invoice_date") },
        { value: "invoice_number", label: data.t.read("rule.filename_piece_invoice_number") },
    ];

    // Define the list of condition types.
    const condition_types = [
        { value: "amount_lower", label: data.t.read("rule.the_amount_is_lower") },
        { value: "amount_greater", label: data.t.read("rule.the_amount_is_greater") },
        { value: "invoice_number_missing", label: data.t.read("rule.invoice_number_is_missing") },
        { value: "supplier_number", label: data.t.read("rule.the_supplier_number") },
        { value: "cost_type", label: data.t.read("rule.the_cost_type_equals") },
        { value: "cost_type_not_equal", label: data.t.read("rule.the_cost_type_not_equals") },
        { value: "client_number", label: data.t.read("rule.the_client_number_equals") },
        { value: "gl_unit_number", label: data.t.read("rule.the_gl_unit_number_equals") },
        { value: "client_number_not_equal", label: data.t.read("rule.the_client_number_not_equals") },
        { value: "invoice_billable", label: data.t.read("rule.invoice_is_set_as") },
        { value: "item_billable", label: data.t.read("rule.item_billable") },
        { value: "invoice_custom", label: data.t.read("rule.invoice_is_custom") },
        { value: "supplier_name_equals", label: data.t.read("rule.supplier_name_equals") },
        { value: "gl_unit_is", label: data.t.read("rule.gl_unit_is") },
        { value: "gl_unit_is_not", label: data.t.read("rule.gl_unit_is_not") },
        { value: "user_has_role", label: data.t.read("rule.user_has_role") },
        { value: "line_item_amount", label: data.t.read("rule.the_line_item_amount") },
        { value: "item_description", label: data.t.read("rule.item_description") },
        { value: "item_cost_type", label: data.t.read("rule.item_cost_type") },
        { value: "item_gl", label: data.t.read("rule.item_gl") },
        ...(Utils.isExportFormat(data, 'elite')
            ? [{ value: "item_gl_class", label: data.t.read("rule.item_gl_class") },
               { value: "item_gl_category", label: data.t.read("rule.item_gl_category") }]
            : []),
        { value: "tax_code", label: data.t.read("rule.invoice_tax_code") },
        { value: "item_tax_code", label: data.t.read("rule.item_tax_code") },
        { value: "currency", label: data.t.read("rule.currency_is") },
        { value: "invoice_model", label: data.t.read("rule.invoice_model") },
        { value: "purchase_and_payment", label: data.t.read("rule.purchase_and_payment") },
        { value: "invoice_type", label: data.t.read("rule.the_invoice_type_is") },
        ...(Utils.userHasPageAccess(data, "expenses")
        ? [
                { value: "category", label: data.t.read("rule.the_category_is")},
                { value: "client_category", label: data.t.read("rule.the_client_category_is")},
            ]
        :[]),
        { value: "user_is", label: data.t.read("rule.the_user") },
        { value: "user_in_department", label: data.t.read("rule.user_in_department") },
        { value: "user_has_function", label: data.t.read("rule.user_has_function") },

    ];

    const condition_logic = [
        {value:"always", label:data.t.read("rule.always")},
        {value:"all", label:data.t.read("rule.if_all_match")},
        {value:"any", label:data.t.read("rule.if_any_match")},

    ]

    // Fetch approvers on component mount.
    useEffect(() => {
        fetchApprovers();
    }, []);

    const fetchApprovers = () => {
        fetch("/client/users/approvers", "GET", {}, (response) => {
            setApprovers(response.data);
        });
    };

    // Helper mapping function for displaying an approver in a Select.
    const approverMap = (approver) => {
        return {
            value: approver?.id_user,
            label: `${approver.first_name} ${approver.last_name}`,
        };
    };

    const loadUsers = (search_query = "", callback = () => {}, selectedUser = null) => {
        const val = sanitizeSearchQuery(search_query);
        cachedFetch(`/client/users/search/${val}`, "GET", {}, (response) => {
            const list = Array.isArray(response.data) ? response.data : [];
            const selectedValue = Array.isArray(selectedUser) ? selectedUser[0] : selectedUser;
            const selectedId = selectedValue?.id_user;
            const options = list
                .filter((user) => user.id_user !== selectedId)
                .map(userMap)
                .filter(Boolean);

            callback(options);
        });
    };

    const loadDepartmentsSearch = (search_query = "", callback = () => {}, selectedDepartment = null) => {
        const val = sanitizeSearchQuery(search_query);
        cachedFetch(`/departments/search/${val}`, "GET", {}, (response) => {
            const list = Array.isArray(response.data) ? response.data : [];
            const selectedValue = Array.isArray(selectedDepartment) ? selectedDepartment[0] : selectedDepartment;
            const selectedId = selectedValue?.id;
            const options = list
                .filter((department) => {
                    const depId = department._id?.$oid || department.id || department._id;
                    return depId !== selectedId;
                })
                .map(departmentSearchMap)
                .filter(Boolean);

            callback(options);
        });
    };

    const approverOptions = [
        {value: {kind:"matter_responsible"}, label:data.t.read("rule.user_responsible_of_matter")},
        {value: {kind:"gl_responsible"}, label:data.t.read("rule.user_responsible_of_gl")},
        ...(approvers.map(approver => ({
            value: {kind: "user", id: approver?.id_user},
            label: `${approver.first_name} ${approver.last_name}`,
        })))
    ]
    
    const baseSelectControlStyles = (baseStyles, state, extra = {}) => ({
        ...baseStyles,
        borderColor: state.isFocused ? "#F39800" : "#d6dae3",
        boxShadow: state.isFocused ? "0 0 0 1px rgba(243, 152, 0, 0.2)" : "none",
        minHeight: "38px",
        ...extra,
    });

    // Common styles for react-select components.
    const styleSelect = {
        control: (baseStyles, state) => baseSelectControlStyles(baseStyles, state),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
    };

    const categorySelect = {
        control: (baseStyles, state) => baseSelectControlStyles(baseStyles, state, {
            minWidth:"150px",
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
    };


    const actionSelect = {
        control: (baseStyles, state) => baseSelectControlStyles(baseStyles, state),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
    };

    const compactSelect = {
        ...styleSelect,
        control: (baseStyles, state) => baseSelectControlStyles(baseStyles, state, {
            minHeight: "34px",
        }),
    };

    const taxCodeOptions = taxCodes.map((code) => ({
        value: code,
        label: code,
    }));
    const costTypeOptions = Array.from(new Set(
        clientImputations.map(imputation => imputation.cost_type).filter(Boolean)
    )).map(costType => ({
        value: costType,
        label: costType,
    }));

    const textOperatorOptions = (conditionType) => [
        {
            value: "contain",
            label: data.t.read("rule.contain")
        },
        {
            value: "not_contain",
            label: data.t.read("rule.not_contain")
        },
        {value: "equal", label: data.t.read("rule.equal")},
        {
            value: "different",
            label: data.t.read("rule.is_different_than")
        },
        {
            value: "starts_with",
            label: data.t.read("rule.starts_with")
        },
        ...(conditionType === "item_gl" ? [{
            value: GL_BETWEEN_OPERATOR,
            label: data.t.read("rule.between")
        }] : []),
    ];

    // Controlled values — a GL account class comes from Elite, so substring and
    // prefix comparisons have nothing to bite on. Same reasoning as tax codes.
    const glClassOperatorOptions = [
        {value: "equal", label: data.t.read("rule.operator_is")},
        {value: "different", label: data.t.read("rule.operator_is_not")},
    ];

    // Tax codes are controlled values, so only exact equality comparisons are meaningful.
    const taxCodeOperatorOptions = [
        {value: "equal", label: data.t.read("rule.equal")},
        {value: "different", label: data.t.read("rule.is_different_than")},
    ];

    // XPEN-3348: the user condition needs its own wording. The shared keys read
    // "equals" / "is different", and in French rule.equal is "est égale à", which
    // would render "l'usager est égale à Alice". Do not simplify this back to the
    // generic rule.<operator> lookup the other condition types use.
    const userOperatorOptions = [
        {value: "equal", label: data.t.read("rule.user_operator_is")},
        {value: "different", label: data.t.read("rule.user_operator_is_not")},
    ];

    // Condition types whose operator comes from the shared operator dropdown below.
    // This is the single source for both rendering that dropdown and laying the row
    // out as three fields, so a new type cannot get one without the other.
    const SHARED_OPERATOR_CONDITION_TYPES = [
        "item_description",
        "item_cost_type",
        "item_gl",
        "item_gl_class",
        "item_gl_category",
        "supplier_number",
        "invoice_model",
        "user_is",
        ...TAX_CODE_CONDITION_TYPES,
    ];

    // line_item_amount is the one exception: it renders its own operator control
    // further down rather than the shared one, but still lays out as three fields.
    const hasOperatorControl = (conditionType) =>
        SHARED_OPERATOR_CONDITION_TYPES.includes(conditionType) || conditionType === "line_item_amount";

    const operatorOptionsFor = (conditionType) => {
        if (conditionType === "user_is") return userOperatorOptions;
        if (GL_ACCOUNT_CONDITION_FIELDS[conditionType]) return glClassOperatorOptions;
        if (TAX_CODE_CONDITION_TYPES.includes(conditionType)) return taxCodeOperatorOptions;
        return textOperatorOptions(conditionType);
    };

    const operatorSelectValue = (condition) => {
        // Read back with the same wording the dropdown offers. The generic
        // rule.<operator> lookup below would render a stored "equal" as "equals",
        // and "est égale à" in French, while the options list says "is".
        if (GL_ACCOUNT_CONDITION_FIELDS[condition.type]) {
            return glClassOperatorOptions.find(
                option => option.value === (condition.operator === "different" ? "different" : "equal"));
        }
        if (condition.type !== "user_is") {
            return {value: condition.operator, label: data.t.read(`rule.${condition.operator}`)};
        }
        // A user condition saved without an operator means "equal", and one saved
        // with any negating operator means "is not" -- the same rules the backend
        // applies (XPEN-3347). Resolving by membership rather than by exact match
        // keeps this picker agreeing with the summary card and with the engine: a
        // stored "not_contain" must never read back here as "is".
        const negating = isNegatingUserOperator(condition.operator);
        return userOperatorOptions.find(option => option.value === (negating ? "different" : "equal"));
    };

    // The engine splits a line condition's value on commas, so "one or more"
    // needs no new shape: the picker joins, and this splits. A code saved before
    // the picker existed -- or one 3E no longer returns -- must still read back
    // as itself rather than vanish from the control.
    const codeOptionsFor = (fieldName) => codeOptions[fieldName] || [];

    // XPEN-3414. The role is a choice rather than a fixed "Approver" because the
    // firm expects a second approval tier; the list is the client's own roles.
    // Approving a line is its own module; without it the rule would have nothing
    // to assign to. A stored level stays visible, so it reads as one to revisit.
    const sectionApprovalTargetOptions = [
        {value: "invoice", label: data.t.read("rule.section_approval_target_invoice")},
        ...(Utils.userHasPageAccess(data, "detail-approval")
            ? [{value: "line_item", label: data.t.read("rule.section_approval_target_line_item")}]
            : []),
    ];

    const sectionApprovalTargetValue = (target) => {
        const stored = target || DEFAULT_SECTION_APPROVAL_VALUE.target;
        return sectionApprovalTargetOptions.find(option => option.value === stored)
            ?? {value: stored, label: data.t.read(`rule.section_approval_target_${stored}`) || stored};
    };

    // A custom role has no `role_*` translation, and the translator returns
    // undefined for a missing key, so the name itself is the label.
    const roleLabel = (role) => data.t.read("role_" + String(role).toLowerCase()) || role;

    // Both role pickers offer the client's own roles, custom ones included, and
    // fall back to the configured defaults when the list comes back empty.
    const clientRoleOptions = (clientRoleNames.length ? clientRoleNames : (data.config?.roles || []))
        .map(role => ({value: role, label: roleLabel(role)}));
    const sectionApprovalRoleOptions = clientRoleOptions;
    const lineItemApprovalRoleOptions = clientRoleOptions;

    // A tenant whose roster does not spell the role the way the rule stores it --
    // a custom naming, or the Approver default on a firm that has none -- would
    // otherwise get a blank required picker that blocks Save with nothing shown.
    // Reading the stored role back as its own entry keeps it visible and
    // changeable, the same way an unknown code does in the value pickers.
    const sectionApprovalRoleValue = (role) => {
        const stored = role || DEFAULT_SECTION_APPROVAL_VALUE.role;
        if (!stored) return null;
        return sectionApprovalRoleOptions.find(option => option.value === stored)
            ?? {value: stored, label: roleLabel(stored)};
    };

    // Optional here: no role keeps every approver, so nothing is preselected.
    const lineItemApprovalRoleValue = (role) => (
        role
            ? lineItemApprovalRoleOptions.find(option => option.value === role) ?? {value: role, label: roleLabel(role)}
            : null
    );

    const updateSectionApprovalValue = (action, patch) => updateAction(
        "value", action,
        {target: {value: {...DEFAULT_SECTION_APPROVAL_VALUE, ...(action.value || {}), ...patch}}});

    const codeSelectValue = (fieldName, value) => (
        String(value ?? "")
            .split(",")
            .map(code => code.trim())
            .filter(Boolean)
            .map(code => codeOptionsFor(fieldName).find(option => option.value === code)
                ?? {value: code, label: glAccountClassLabel(code)})
    );

    const codeSelectChange = (fieldName, condition, selection) => updateCondition(
        "value", condition,
        {target: {value: (selection || []).map(option => option.value).join(",")}});

    const updateGlBetweenValue = (condition, property, rawValue) => {
        const currentValue = normalizeGlBetweenValue(condition.value);
        const integerValue = rawValue.replace(/\D/g, "");
        updateCondition("value", condition, {
            target: {value: {...currentValue, [property]: integerValue}},
        });
    };
    const updateMileageSplitValue = (actionObj, patch) => {
        const updatedStep = {
            ...localStep,
            [actionsKey]: localStep[actionsKey].map(action => {
                if (action.id !== actionObj.id) return action;
                return {
                    ...action,
                    value: {
                        ...(typeof action.value === "object" && action.value !== null ? action.value : {}),
                        ...patch,
                    },
                };
            }),
        };
        setLocalStep(updatedStep);
    };

    const updateTipSplitValue = (actionObj, patch) => {
        const updatedStep = {
            ...localStep,
            [actionsKey]: localStep[actionsKey].map(action => {
                if (action.id !== actionObj.id) return action;
                return {
                    ...action,
                    value: {
                        ...(typeof action.value === "object" && action.value !== null ? action.value : {}),
                        ...patch,
                    },
                };
            }),
        };
        setLocalStep(updatedStep);
    };

    const updateRenameFileValue = (actionObj, patch) => {
        const updatedStep = {
            ...localStep,
            [actionsKey]: localStep[actionsKey].map(action => {
                if (action.id !== actionObj.id) return action;
                return {
                    ...action,
                    value: {
                        ...DEFAULT_RENAME_FILE_VALUE,
                        ...(typeof action.value === "object" && action.value !== null ? action.value : {}),
                        ...patch,
                    },
                };
            }),
        };
        setLocalStep(updatedStep);
    };

    const updateRenameFilePiece = (actionObj, index, piece) => {
        const currentValue = {
            ...DEFAULT_RENAME_FILE_VALUE,
            ...(typeof actionObj.value === "object" && actionObj.value !== null ? actionObj.value : {}),
        };
        const pieces = [...(currentValue.pieces || [])];
        pieces[index] = piece;
        updateRenameFileValue(actionObj, {pieces});
    };

    const updateInvoiceApprovalValue = (actionObj, patch) => {
        const updatedStep = {
            ...localStep,
            [actionsKey]: localStep[actionsKey].map(action => {
                if (action.id !== actionObj.id) return action;
                const currentValue = typeof action.value === "object" && action.value !== null
                    ? action.value
                    : {...DEFAULT_INVOICE_APPROVAL_VALUE};
                return {
                    ...action,
                    value: {
                        ...currentValue,
                        ...patch,
                    },
                };
            }),
        };
        setLocalStep(updatedStep);
    };

    const setActionValue = (actionObj, value) => {
        setLocalStep({
            ...localStep,
            [actionsKey]: localStep[actionsKey].map(a => a.id === actionObj.id ? {...a, value} : a),
        });
    };

    const anyOfSpecs = (actionObj) => Array.isArray(actionObj.value) ? actionObj.value : [{...DEFAULT_ANY_OF_SPEC}];

    const updateAnyOfSpec = (actionObj, idx, patch) => {
        const specs = anyOfSpecs(actionObj);
        setLocalStep({
            ...localStep,
            [actionsKey]: localStep[actionsKey].map(a => a.id === actionObj.id
                ? {...a, value: specs.map((s, i) => i === idx ? {...s, ...patch} : s)}
                : a),
        });
    };

    const addAnyOfSpec = (actionObj) => {
        const specs = anyOfSpecs(actionObj);
        setLocalStep({
            ...localStep,
            [actionsKey]: localStep[actionsKey].map(a => a.id === actionObj.id
                ? {...a, value: [...specs, {...DEFAULT_ANY_OF_SPEC}]}
                : a),
        });
    };

    const removeAnyOfSpec = (actionObj, idx) => {
        const specs = anyOfSpecs(actionObj);
        const next = specs.filter((_, i) => i !== idx);
        setLocalStep({
            ...localStep,
            [actionsKey]: localStep[actionsKey].map(a => a.id === actionObj.id
                ? {...a, value: next.length ? next : [{...DEFAULT_ANY_OF_SPEC}]}
                : a),
        });
    };

    const updateLineItemApprovalValue = (actionObj, patch) => {
        const updatedStep = {
            ...localStep,
            [actionsKey]: localStep[actionsKey].map(action => {
                if (action.id !== actionObj.id) return action;
                const currentValue = typeof action.value === "object" && action.value !== null
                    ? action.value
                    : {...DEFAULT_LINE_ITEM_APPROVAL_VALUE};
                return {
                    ...action,
                    value: {
                        ...currentValue,
                        ...patch,
                    },
                };
            }),
        };
        setLocalStep(updatedStep);
    };

    const workflowValueModeOptions = [
        { value: "manual", label: data.t.read("rule.manual_value") },
        { value: "gl_natural_prefix", label: data.t.read("rule.first_4_digits_of_gl_natural") },
    ];

    const renderFieldValueInput = (action) => {
        const isGLSection = action.field === "GLSection";
        const isDynamicValue = isGLSectionWorkflowVariable(action.value);

        return (
            <div className={`col-8 ${isGLSection ? "workflow-edit-step-modal__field-value-row" : ""}`}>
                {isGLSection && (
                    <Select
                        styles={actionSelect}
                        name="workflow value mode select"
                        className="workflow-edit-step-modal__value-mode-select"
                        value={workflowValueModeOptions.find(option => option.value === (isDynamicValue ? "gl_natural_prefix" : "manual"))}
                        options={workflowValueModeOptions}
                        classNamePrefix="select"
                        onChange={(choice) =>
                            updateAction("value", action, {
                                target: {value: choice.value === "gl_natural_prefix" ? GL_SECTION_WORKFLOW_VARIABLE : ""},
                            })
                        }
                    />
                )}
                {!isDynamicValue && (
                    <input
                        type="text"
                        required
                        className="form-control workflow-edit-step-modal__control"
                        placeholder={action.field}
                        value={typeof action.value === "string" ? action.value : ""}
                        onChange={(e) =>
                            updateAction("value", action, e)
                        }
                    />
                )}
            </div>
        );
    };

    const actionHasSubfields = (actionType) => [
        "achat_and_payment",
        REQUEST_INVOICE_APPROVAL,
        REQUEST_INVOICE_ANY_OF_APPROVAL,
        REQUEST_LINE_ITEM_APPROVAL,
        REQUEST_SECTION_APPROVAL,
        "append_approver_list",
        "exclude_approver_roles",
        "change_status",
        "change_due_date",
        "change_item_detail",
        "change_invoice_default_field_value",
        "send_email",
        "change_tax_codes",
        "change_item_tax_code",
        "change_unit",
        "change_currency",
        "split_mileage_by_secondary_rate",
        "split_tip_into_separate_item",
        "set_invoice_number",
        RENAME_INVOICE_FILE,
        "split_items",
        "change_all_totals",
        "tax_deduction",
        "change_user",
        "change_item_description",
        "change_cheque_reference",
    ].includes(actionType);

    // If no step is selected, render nothing.
    if (!selectedStep) return null;


    return (
        <form className="workflow-edit-step-modal" autoComplete="off" onSubmit={(e) => {
            e.preventDefault();
            // Checked here, not only when the workflow is saved: otherwise the modal
            // accepts the step, closes, and the problem surfaces later as a generic
            // warning with no way to tell which side is at fault. A branch
            // precondition is exempt — emptying it is how it gets removed.
            if (!conditionsOnly) {
                const offending = [PRIMARY_SIDE, ALTERNATE_SIDE].find((candidate) => {
                    const keys = SIDE_KEYS[candidate];
                    const conditions = localStep[keys.conditions] || [];
                    const actions = localStep[keys.actions] || [];
                    // The primary side is always in use; the alternate one only once
                    // something has been put on it.
                    const inUse = candidate === PRIMARY_SIDE
                        || conditions.length > 0 || actions.length > 0;
                    if (!inUse) return false;
                    const missingCondition = localStep[keys.logic] !== "always"
                        && conditions.length === 0;
                    return missingCondition || actions.length === 0;
                });
                if (offending) {
                    // Show the side at fault rather than reporting it from the other one.
                    setSide(offending);
                    const keys = SIDE_KEYS[offending];
                    const missingCondition = localStep[keys.logic] !== "always"
                        && (localStep[keys.conditions] || []).length === 0;
                    toast.error(data.t.read(missingCondition
                        ? "rule.condition_required_unless_always"
                        : "rule.action_required"));
                    return;
                }
            }
            onSubmit();
        }}>
            {!conditionsOnly && (
            <div className="workflow-edit-step-modal__section">
                <label className="workflow-edit-step-modal__section-label">{data.t.read("name")}</label>
                <input
                    type="text"
                    onFocus={(e) => e.target.select()}
                    required
                    className="form-control workflow-edit-step-modal__name-input workflow-edit-step-modal__control"
                    placeholder={data.t.read("rule.name")}
                    value={localStep.name ?? ""}
                    onChange={(e) =>
                        setLocalStep({
                            ...localStep,
                            name: e.target.value,
                        })
                    }
                />
            </div>
            )}
            {!conditionsOnly && (
            <div className="workflow-edit-step-modal__section pb-0">
                <div className="workflow-edit-step-modal__logic-group d-inline-flex overflow-hidden rounded">
                    {[
                        {value: PRIMARY_SIDE, label: data.t.read("rule.actions_when_matched")},
                        {value: ALTERNATE_SIDE, label: data.t.read("rule.actions_otherwise")},
                    ].map((option, index) => {
                        const keys = SIDE_KEYS[option.value];
                        const count = (localStep[keys.actions] || []).length;
                        // Marked as soon as the side is in use, so one holding conditions
                        // but no action yet does not look empty.
                        const inUse = count > 0 || (localStep[keys.conditions] || []).length > 0;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                data-testid={`step-side-tab-${option.value}`}
                                onClick={() => setSide(option.value)}
                                className={`workflow-edit-step-modal__logic-button ${index === 0 ? "workflow-edit-step-modal__logic-button--left" : "workflow-edit-step-modal__logic-button--right"} ${side === option.value ? "workflow-edit-step-modal__logic-button--active" : ""}`}
                            >
                                {option.label}{inUse ? ` (${count})` : ""}
                            </button>
                        );
                    })}
                </div>
            </div>
            )}
            <div className="workflow-edit-step-modal__section">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <label className="workflow-edit-step-modal__section-label mb-0">
                        {conditionsOnly ? data.t.read("rule.branch_precondition") : "Conditions"}
                    </label>
                    <button
                        type="button"
                        disabled={localStep[logicKey] === "always"}
                        onClick={() => addCondition()}
                        className="btn btn-link btn-sm p-0 font-weight-bold text-primary"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-1"/> Condition
                    </button>
                </div>
                {conditionsOnly && (
                    <div className="small text-muted mb-3" data-testid="branch-precondition-hint">
                        {data.t.read("rule.branch_precondition_hint")}
                    </div>
                )}
                <div className="mb-3">
                    <div className="small font-weight-bold text-uppercase text-muted mb-2">
                        {data.t.read("rule.run_actions")}
                    </div>
                    <div className="workflow-edit-step-modal__logic-group d-inline-flex overflow-hidden rounded">
                        {condition_logic.map((option, index) => {
                            const isActive = (localStep[logicKey] || "all") === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => setLocalStep({...localStep, [logicKey]: option.value})}
                                    className={`workflow-edit-step-modal__logic-button ${index === 0 ? "workflow-edit-step-modal__logic-button--left" : "workflow-edit-step-modal__logic-button--right"} ${isActive ? "workflow-edit-step-modal__logic-button--active" : ""}`}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div>
                            {localStep[logicKey] !== "always" && (localStep[conditionsKey] || []).map((c, idx) => {
                                return (
                                    <div key={c.id} className="workflow-edit-step-modal__card">
                                        <div className="d-flex justify-content-between align-items-start">
                                            {/* Condition left side */}
                                            <div className={`d-flex align-content-center flex-grow-1 workflow-edit-step-modal__condition-row${
                                                hasOperatorControl(c.type) ? " workflow-edit-step-modal__condition-row--thirds" : ""
                                            }`}>
                                                <div className="col-6">
                                                    <Select
                                                        menuPortalTarget={document.body}
                                                        styles={compactSelect}
                                                        name="condition type select"
                                                        value={
                                                            condition_types.find((option) => option.value === c.type) || ""
                                                        }
                                                        options={condition_types}
                                                        classNamePrefix="select"
                                                        onChange={(choice) =>
                                                            updateCondition("type", c, {target: {value: choice.value}})
                                                        }
                                                    />
                                                </div>

                                                {/* Operator / Value fields */}
                                                <div className="d-flex align-content-center col-6 workflow-edit-step-modal__condition-fields">
                                                    {/* Some conditions have an "operator" dropdown */}
                                                    {SHARED_OPERATOR_CONDITION_TYPES.includes(c.type) ? (
                                                        <div className="col-6 p-0 m-0">
                                                            <Select
                                                                menuPortalTarget={document.body}
                                                                styles={styleSelect}
                                                                name={c.type === "user_is"
                                                                    ? "user-operator-select"
                                                                    : "operator select"}
                                                                value={operatorSelectValue(c)}
                                                                options={operatorOptionsFor(c.type)}
                                                                classNamePrefix="select"
                                                                onChange={(choice) =>
                                                                    updateCondition("operator", c, {
                                                                        target: {value: choice.value},
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    ) : null}

                                                    {c.type === "line_item_amount" ? (
                                                        <div className="col-6 p-0 m-0">
                                                            <Select
                                                                menuPortalTarget={document.body}
                                                                styles={styleSelect}
                                                                name="operator select"
                                                                value={{
                                                                    value: c.operator,
                                                                    label: data.t.read(`rule.${c.operator}`),
                                                                }}
                                                                options={[
                                                                    {
                                                                        value: "greater",
                                                                        label: data.t.read("rule.greater")
                                                                    },
                                                                    {value: "lower", label: data.t.read("rule.lower")},
                                                                    {
                                                                        value: "equals",
                                                                        label: data.t.read("rule.equals")
                                                                    },
                                                                ]}
                                                                classNamePrefix="select"
                                                                onChange={(choice) =>
                                                                    updateCondition("operator", c, {
                                                                        target: {value: choice.value},
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    ) : null}

                                                    <div className="align-items-center d-flex flex-grow-1">
                                                        {/* Billable / Non-billable */}
                                                        {BILLABLE_CONDITION_TYPES.includes(c.type) ? (
                                                            <Select
                                                                menuPortalTarget={document.body}
                                                                styles={styleSelect}
                                                                name="value select"
                                                                value={{
                                                                    value: c.value,
                                                                    label: data.t.read(
                                                                        c.value === "1" ? "rule.billable" : "rule.non_billable"
                                                                    ),
                                                                }}
                                                                options={[
                                                                    {value: "1", label: data.t.read("rule.billable")},
                                                                    {
                                                                        value: "0",
                                                                        label: data.t.read("rule.non_billable")
                                                                    },
                                                                ]}
                                                                classNamePrefix="select"
                                                                onChange={(choice) =>
                                                                    updateCondition("value", c, {
                                                                        target: {value: choice.value},
                                                                    })
                                                                }
                                                            />
                                                        ) : c.type === "user_has_role" ? (
                                                            <Select
                                                                menuPortalTarget={document.body}
                                                                required
                                                                styles={styleSelect}
                                                                name="user_has_role"
                                                                value={
                                                                    c.role
                                                                        ? {
                                                                            value: c.role,
                                                                            label: data.t.read(
                                                                                "role_" + c.role.toLowerCase()
                                                                            ),
                                                                        }
                                                                        : null
                                                                }
                                                                options={data.config.roles.map((r) => ({
                                                                    value: r,
                                                                    label: data.t.read("role_" + r.toLowerCase()),
                                                                }))}
                                                                placeholder={data.t.read("client_table.role").toLowerCase()}
                                                                classNamePrefix="select"
                                                                onChange={(choice) =>
                                                                    updateCondition("role", c, {
                                                                        target: {value: choice.value},
                                                                    })
                                                                }
                                                            />
                                                        ) : c.type === "user_has_function" ? (
                                                            <div className="w-100">

                                                            <Select
                                                                menuPortalTarget={document.body}
                                                                required
                                                                styles={styleSelect}
                                                                name="user_has_function"
                                                                value={
                                                                    c.value
                                                                        ? functionMap({name: c.value})
                                                                        : null
                                                                }
                                                                options={functions.map(functionMap)}
                                                                placeholder={data.t.read("function")}
                                                                classNamePrefix="select"
                                                                onChange={(choice) =>
                                                                    updateCondition("value", c, {
                                                                        target: {value: choice.value},
                                                                    })
                                                                }
                                                            />
                                                            </div>
                                                        ) : c.type === "user_is" ? (
                                                            <div className="w-100">
                                                                <AsyncSelect
                                                                    key={`user-is-${c.id}`}
                                                                    styles={styleSelect}
                                                                    // XPEN-3348: the dropdown shows "Name (email)" so two people
                                                                    // with the same name can be told apart, but a selected chip
                                                                    // shows the name alone. Service accounts carry addresses long
                                                                    // enough to fill the whole control on their own.
                                                                    formatOptionLabel={(option, meta) =>
                                                                        (meta.context === "value" ? userChipLabel(option) : option.label)
                                                                    }
                                                                    // XPEN-3348: this menu is deliberately NOT portalled, unlike the
                                                                    // others in this modal. react-select only observes the control's
                                                                    // size when it portals the menu, to keep the menu anchored --
                                                                    // and this control is the one that grows, because it holds a
                                                                    // list of chips and keeps its menu open while you pick. Growing
                                                                    // a watched element mid-frame is what raises "ResizeObserver
                                                                    // loop completed with undelivered notifications". Without the
                                                                    // portal there is no observer, so the list can grow freely and
                                                                    // the menu can stay open, which XPEN-3072 asked for.
                                                                    // menuPlacement="auto" flips the menu upward when a condition
                                                                    // sits low in a long modal, which the portal used to handle.
                                                                    menuPlacement="auto"
                                                                    maxMenuHeight={160}
                                                                    cacheOptions
                                                                    defaultOptions
                                                                    isMulti
                                                                    isClearable
                                                                    closeMenuOnSelect={false}
                                                                    classNamePrefix="select"
                                                                    placeholder={data.t.read("rule.select_users")}
                                                                    name="user-is-select"
                                                                    loadOptions={loadUsers}
                                                                    // User IDs provide stable option identity across separate async searches,
                                                                    // preventing the same user from being selected more than once.
                                                                    getOptionValue={(option) => option.value?.id_user}
                                                                    // Existing workflows store one user object. New workflows store an array,
                                                                    // so normalize both shapes for editing without requiring a migration.
                                                                    value={(Array.isArray(c.value) ? c.value : [c.value].filter(Boolean))
                                                                        .map(userMap)
                                                                        .filter(Boolean)}
                                                                    onChange={(selected) => {
                                                                        // Persist every selected user. The evaluator treats this list as
                                                                        // an unordered "any selected user" condition.
                                                                        const users = (selected || [])
                                                                            .map((option) => option.value)
                                                                            .filter(Boolean);
                                                                        updateCondition("value", c, {
                                                                            target: {value: users},
                                                                        });
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : c.type === "user_in_department" ? (
                                                            <div className="w-100">
                                                                <AsyncSelect
                                                                    key={`user-department-${c.id}`}
                                                                    styles={styleSelect}
                                                                    menuPortalTarget={document.body}
                                                                    cacheOptions
                                                                    defaultOptions
                                                                    isClearable
                                                                    classNamePrefix="select"
                                                                    placeholder={data.t.read("rule.select_department")}
                                                                    name="user-department-select"
                                                                    loadOptions={loadDepartmentsSearch}
                                                                    value={
                                                                        c.value
                                                                            ? departmentSearchMap(Array.isArray(c.value) ? c.value[0] : c.value)
                                                                            : null
                                                                    }
                                                                    onChange={(selected) => {
                                                                        const department = selected ? selected.value : null;
                                                                        updateCondition("value", c, {
                                                                            target: {value: department},
                                                                        });
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : c.type === "invoice_custom" ? (
                                                            <Select
                                                                menuPortalTarget={document.body}
                                                                styles={styleSelect}
                                                                name="invoice custom select"
                                                                value={{
                                                                    value: c.value,
                                                                    label: data.t.read(
                                                                        c.value === "1"
                                                                            ? "rule.invoice_custom"
                                                                            : "rule.invoice_not_custom"
                                                                    ),
                                                                }}
                                                                options={[
                                                                    {
                                                                        value: "1",
                                                                        label: data.t.read("rule.invoice_custom"),
                                                                    },
                                                                    {
                                                                        value: "0",
                                                                        label: data.t.read("rule.invoice_not_custom"),
                                                                    },
                                                                ]}
                                                                classNamePrefix="select"
                                                                onChange={(choice) =>
                                                                    updateCondition("value", c, {
                                                                        target: {value: choice.value},
                                                                    })
                                                                }
                                                            />
                                                        ) : c.type === "line_item_amount"
                                                        || c.type === "amount_lower" || c.type === "amount_greater" ? (
                                                            <MoneyInput
                                                                className="workflow-edit-step-modal__control"
                                                                value={c.value}
                                                                required
                                                                onChange={(value) =>
                                                                    updateCondition("value", c, {
                                                                        target: {value: value},
                                                                    })
                                                                }
                                                                locale={`${data.t.lang}-${data.t.lang.toUpperCase()}`}
                                                                placeholder={data.t.read("rule.amount")}
                                                            />
                                                        ) : TAX_CODE_CONDITION_TYPES.includes(c.type) ? (
                                                            <Select
                                                                menuPortalTarget={document.body}
                                                                required
                                                                styles={styleSelect}
                                                                name="tax code condition value select"
                                                                value={taxCodeOptions.find((option) => option.value === c.value) ?? null}
                                                                options={taxCodeOptions}
                                                                placeholder={data.t.read("rule.tax_code")}
                                                                classNamePrefix="select"
                                                                onChange={(choice) =>
                                                                    updateCondition("value", c, {
                                                                        target: {value: choice?.value ?? ""},
                                                                    })
                                                                }
                                                            />
                                                        ) : c.type === "category" ? (
                                                                    <Select
                                                                        styles={categorySelect}
                                                                        name="category select"
                                                                        placeholder={data.t.read("select_placeholder")}
                                                                        value={selectedXpensCategoryOption(c.value)}
                                                                        options={xpensCategoryOptions}
                                                                        classNamePrefix="select"
                                                                        required
                                                                        onChange={(choice) =>
                                                                            updateCondition("value", c, {
                                                                                target: {value: choice?.value ?? ""},
                                                                            })
                                                                        }
                                                                    />

                                                            )
                                                            :
                                                            c.type === "client_category" ? (
                                                                    <Select
                                                                        styles={categorySelect}
                                                                        name="client category select"
                                                                        placeholder={data.t.read("select_placeholder")}
                                                                        value={selectedClientCategoryOption(c.value)}
                                                                        options={clientCategoryOptions}
                                                                        classNamePrefix="select"
                                                                        required
                                                                        onChange={(choice) =>
                                                                            updateCondition("value", c, {
                                                                                target: {value: choice?.value ?? ""},
                                                                            })
                                                                        }
                                                                    />
                                                            )
                                                            :
                                                            c.type === "purchase_and_payment" ? (
                                                                    <Select
                                                                        menuPortalTarget={document.body}
                                                                        styles={categorySelect}
                                                                        className={"ml-2"}
                                                                        placeholder={data.t.read("select_placeholder")}
                                                                        name="operator select"
                                                                        value={c.value ? { value: c.value, label: data.t.read(`rule.${c.value}`)} : null}
                                                                        options={[
                                                                            {value: "activated", label: data.t.read("rule.activated")},
                                                                            {value: "disabled", label: data.t.read("rule.disabled")},
                                                                        ]}
                                                                        classNamePrefix="select"
                                                                        onChange={(choice) =>
                                                                            updateCondition("value", c, {
                                                                                target: {value: choice.value},
                                                                            })
                                                                        }
                                                                    />
                                                                ) : c.type === "invoice_type" ? (
                                                                    <Select
                                                                        menuPortalTarget={document.body}
                                                                        styles={categorySelect}
                                                                        className={"ml-2"}
                                                                        placeholder={data.t.read("select_placeholder")}
                                                                        name="invoice type select"
                                                                        value={c.value ? { value: c.value, label: data.t.read(`rule.invoice_type_${c.value}`)} : null}
                                                                        options={[
                                                                            // { value: "regular", label: data.t.read("rule.invoice_type_regular") },
                                                                            { value: "xpense", label: data.t.read("rule.invoice_type_xpense") },
                                                                            // { value: "credit_card", label: data.t.read("rule.invoice_type_credit_card") },
                                                                        ]}
                                                                        classNamePrefix="select"
                                                                        onChange={(choice) =>
                                                                            updateCondition("value", c, {
                                                                                target: { value: choice.value },
                                                                            })
                                                                        }
                                                                    />
                                                                )
                                                            : c.type === "supplier_number" || c.type === "supplier_number_not_equal" ? (
                                                                    <div>
                                                                        <FormTextfield format={"data"}
                                                                                       value={c.value}
                                                                                       locked={null}
                                                                                       validateChange={val => updateCondition("value", c, {target: {value: val}})}
                                                                                       searchURL="/suppliers/search_codes/"
                                                                                       hideAddOperation={true}
                                                                                       hideNoResults={true}
                                                                                       classes={"workflow-edit-step-modal__control p-2 border"}
                                                                        />
                                                                    </div>
                                                                )
                                                                : c.type === "cost_type" || c.type === "cost_type_not_equal" || c.type === "item_cost_type" ? (
                                                                        <div>
                                                                            <FormTextfield format={"data"}
                                                                                           value={c.value}
                                                                                           locked={null}
                                                                                           splitValues={true}
                                                                                           validateChange={val => updateCondition("value", c, {target: {value: val}})}
                                                                                           searchURL="/cost_type/search/"
                                                                                           hideAddOperation={true}
                                                                                           hideNoResults={true}
                                                                                           classes={"workflow-edit-step-modal__control p-2 border"}/>
                                                                        </div>

                                                                    )
                                                                    : c.type === "item_gl" && c.operator === GL_BETWEEN_OPERATOR ? (
                                                                            <div className="d-flex ml-2 align-items-center w-100">
                                                                                <input
                                                                                    type="text"
                                                                                    inputMode="numeric"
                                                                                    pattern="[0-9]*"
                                                                                    required
                                                                                    aria-label={data.t.read("rule.min_value")}
                                                                                    className="form-control workflow-edit-step-modal__control mr-2"
                                                                                    placeholder={data.t.read("rule.min_value")}
                                                                                    value={normalizeGlBetweenValue(c.value).min}
                                                                                    onChange={(e) => updateGlBetweenValue(c, "min", e.target.value)}
                                                                                />
                                                                                <span className="mx-1">{data.t.read("rule.and")}</span>
                                                                                <input
                                                                                    type="text"
                                                                                    inputMode="numeric"
                                                                                    pattern="[0-9]*"
                                                                                    required
                                                                                    aria-label={data.t.read("rule.max_value")}
                                                                                    className="form-control workflow-edit-step-modal__control ml-2"
                                                                                    placeholder={data.t.read("rule.max_value")}
                                                                                    value={normalizeGlBetweenValue(c.value).max}
                                                                                    onChange={(e) => updateGlBetweenValue(c, "max", e.target.value)}
                                                                                />
                                                                            </div>

                                                                        )
                                                                    : c.type === "item_gl"  ? (
                                                                            <div>
                                                                                <FormTextfield format={"data"}
                                                                                               value={c.value}
                                                                                               splitValues={true}
                                                                                               locked={null}
                                                                                               validateChange={val => updateCondition("value", c, {target: {value: val}})}
                                                                                               searchURL="/gl/nat/search/"
                                                                                               hideAddOperation={true}
                                                                                               hideNoResults={true}
                                                                                               classes={"workflow-edit-step-modal__control p-2 border"}/>
                                                                            </div>

                                                                        )
                                                                    : GL_ACCOUNT_CONDITION_FIELDS[c.type] ? (
                                                                            // The wrapper carries the width. __control must not: it also
                                                                            // paints a border and a fixed height meant for a plain input,
                                                                            // which react-select would draw its own control inside.
                                                                            <div className="flex-grow-1"
                                                                                 data-condition-value={c.type}>
                                                                                <Select
                                                                                    menuPortalTarget={document.body}
                                                                                    isMulti
                                                                                    required
                                                                                    styles={styleSelect}
                                                                                    name={`${GL_ACCOUNT_CONDITION_FIELDS[c.type].name} select`}
                                                                                    classNamePrefix="select"
                                                                                    options={codeOptionsFor(GL_ACCOUNT_CONDITION_FIELDS[c.type].field)}
                                                                                    formatOptionLabel={option =>
                                                                                        glAccountClassNode(option.value, option.description)}
                                                                                    value={codeSelectValue(GL_ACCOUNT_CONDITION_FIELDS[c.type].field, c.value)}
                                                                                    placeholder={data.t.read("rule.value")}
                                                                                    noOptionsMessage={() => data.t.read("no_results_found")}
                                                                                    onChange={selection =>
                                                                                        codeSelectChange(GL_ACCOUNT_CONDITION_FIELDS[c.type].field, c, selection)}
                                                                                />
                                                                            </div>

                                                                        )
                                                                        : c.type === "gl_unit_is" || c.type === "gl_unit_is_not" || c.type === "gl_unit_number" ? (
                                                                                <div>
                                                                                    <FormTextfield format={"data"}
                                                                                                   value={c.value}
                                                                                                   locked={null}
                                                                                                   splitValues={true}
                                                                                                   validateChange={val => updateCondition("value", c, {target: {value: val}})}
                                                                                                   searchURL="/suppliers/unit/search/"
                                                                                                   hideAddOperation={true}
                                                                                                   hideNoResults={true}
                                                                                                   classes={"workflow-edit-step-modal__control p-2 border"}/>
                                                                                </div>

                                                                            )
                                                                        : c.type === "supplier_name_equals" ? (
                                                                                <div className={"d-flex flex-grow-1 "}>
                                                                                    <FormTextfield format={"data"}
                                                                                                   value={c.value}
                                                                                                   locked={null}
                                                                                                   splitValues={true}
                                                                                                   validateChange={val => updateCondition("value", c, {target: {value: val}})}
                                                                                                   searchURL="/suppliers/search/"
                                                                                                   hideAddOperation={true}
                                                                                                   hideNoResults={true}
                                                                                                   classes={"workflow-edit-step-modal__control p-2 border"}/>
                                                                                </div>

                                                                            )
                                                                            :
                                                                                c.type === "invoice_number_missing" ? null
                                                                                    :
                                                                            (
                                                            // Generic text input for other condition types
                                                            <input
                                                                type="text"
                                                                required
                                                                className="form-control align-items-center workflow-edit-step-modal__control"
                                                                placeholder={data.t.read("rule.value")}
                                                                value={c.value}
                                                                onChange={(e) => updateCondition("value", c, e)}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Condition remove icon */}
                                            <div className="workflow-edit-step-modal__card-remove">
                                                {/* Only a step's primary side must keep one condition
                                                    (validateStep); a precondition and the alternate side
                                                    are both allowed to have none. */}
                                                {(conditionsOnly || side === ALTERNATE_SIDE
                                                  || (localStep[conditionsKey] || []).length > 1) && (
                                                    <FontAwesomeIcon
                                                        className="text-muted"
                                                        data-testid={`remove-condition-${c.id}`}
                                                        icon={faTrash}
                                                        type="button"
                                                        onClick={() => removeCondition(c)}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                </div>
            </div>
            {!conditionsOnly && (
            <div className="workflow-edit-step-modal__section">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <label className="workflow-edit-step-modal__section-label mb-0">Actions</label>
                    <button
                        type="button"
                        onClick={() => updateAction("addAction", null, {target: {value: null}})}
                        className="btn btn-link btn-sm p-0 font-weight-bold text-primary"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-1"/> Action
                    </button>
                </div>
                <div>
                            {(localStep[actionsKey] || []).map((action, idx) => (
                                <div key={idx} className="workflow-edit-step-modal__card">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="p-2 col-11">
                                            {/* Action Type */}
                                                <Select
                                                    styles={compactSelect}
                                                    isDisabled={
                                                    (action.type === REQUEST_INVOICE_APPROVAL || action.type === "request_line_item_approval") &&
                                                    !Utils.userHasPageAccess(data, "approval")
                                                }
                                                name="action type select"
                                                value={
                                                    action_types.find((option) => option.value === action.type) || {
                                                        value: action.type,
                                                        label: data.t.read(`rule.${action.type}`),
                                                    }
                                                }
                                                options={action_types}
                                                classNamePrefix="select"
                                                onChange={(choice) =>
                                                    updateAction("type", action, {
                                                        target: {value: choice.value},
                                                    })
                                                }
                                            />
                                            {/* Depending on action.type, show relevant sub-fields */}
                                            {actionHasSubfields(action.type) && (
                                            <div className="d-flex mt-4 col-12 mb-4 flex-wrap">
                                                {action.type === "achat_and_payment" && (
                                                    <div className="border rounded p-2 col-12">
                                                        {/* Payment mode */}
                                                        <div className="row mb-2">
                                                            <div className="col-3 align-self-center">
                                                                {data.t.read("payment_mode")}
                                                            </div>
                                                            <div className="col-9">
                                                                <Select
                                                                    required
                                                                    styles={styleSelect}
                                                                    name="payment_mode"
                                                                    value={action.payment_mode || ""}
                                                                    options={
                                                                        data.config.payment_modes
                                                                            ? data.config.payment_modes.map((p) => ({
                                                                                value: p.id,
                                                                                label: p.name,
                                                                            }))
                                                                            : []
                                                                    }
                                                                    placeholder={data.t.read("payment_mode")}
                                                                    classNamePrefix="select"
                                                                    onChange={(choice) =>
                                                                        updateAction("payment_mode", action, {
                                                                            target: {value: choice},
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                        {/* Bank */}
                                                        <div className="row">
                                                            <div className="col-3 align-self-center">
                                                                {data.t.read("bank")}
                                                            </div>
                                                            <div className="col-9">
                                                                <Select
                                                                    required
                                                                    styles={styleSelect}
                                                                    name="bank"
                                                                    value={action.bank || ""}
                                                                    options={
                                                                        data.config.banks
                                                                            ? data.config.banks.map((p) => ({
                                                                                value: p.key,
                                                                                label: p.value,
                                                                            }))
                                                                            : []
                                                                    }
                                                                    placeholder={data.t.read("bank")}
                                                                    classNamePrefix="select"
                                                                    onChange={(choice) =>
                                                                        updateAction("bank", action, {
                                                                            target: {value: choice},
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {action.type === REQUEST_INVOICE_APPROVAL && (
                                                    <div className="border rounded p-2 col-12">
                                                        <div className="row mb-2">
                                                            <div className="col-3 align-self-center">
                                                                {data.t.read("approver")}
                                                            </div>
                                                            <div className="col-9">
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    required
                                                                    styles={actionSelect}
                                                                    isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                    name="invoice approval source"
                                                                    value={invoiceApprovalSourceOptions.find((option) => option.value === action.value?.approver_source) || invoiceApprovalSourceOptions[0]}
                                                                    options={invoiceApprovalSourceOptions}
                                                                    placeholder={data.t.read("approver")}
                                                                    classNamePrefix="select"
                                                                    onChange={(choice) =>
                                                                        updateInvoiceApprovalValue(action, {
                                                                            approver_source: choice.value,
                                                                            user_id: "",
                                                                            department: "",
                                                                            function: "",
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        </div>

                                                        {action.value?.approver_source === "manual_user" && (
                                                            <div className="row mb-2">
                                                                <div className="col-3 align-self-center">
                                                                    {data.t.read("user")}
                                                                </div>
                                                                <div className="col-9">
                                                                    <Select
                                                                        menuPortalTarget={document.body}
                                                                        required
                                                                        styles={actionSelect}
                                                                        isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                        name="invoice approval user"
                                                                        value={
                                                                            approvers
                                                                                .filter((a) => a.id_user === action.value?.user_id)
                                                                                .map(approverMap) ?? null
                                                                        }
                                                                        options={approvers.map(approverMap)}
                                                                        placeholder={data.t.read("approver")}
                                                                        classNamePrefix="select"
                                                                        onChange={(choice) =>
                                                                            updateInvoiceApprovalValue(action, {user_id: choice.value})
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}

                                                        {action.value?.approver_source === "department" && (
                                                            <>
                                                                <div className="row mb-2">
                                                                    <div className="col-3 align-self-center">
                                                                        {data.t.read("department")}
                                                                    </div>
                                                                    <div className="col-9">
                                                                        <Select
                                                                            required
                                                                            styles={styleSelect}
                                                                            isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                            name="invoice approval departments"
                                                                            value={
                                                                                departments
                                                                                    .filter((a) => a._id?.$oid === action.value?.department)
                                                                                    .map(departmentMap) ?? null
                                                                            }
                                                                            options={departments.map(departmentMap)}
                                                                            placeholder={data.t.read("department")}
                                                                            classNamePrefix="select"
                                                                            onChange={(choice) =>
                                                                                updateInvoiceApprovalValue(action, {department: choice.value})
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="row mb-2">
                                                                    <div className="col-3 align-self-center">
                                                                        {data.t.read("function")}
                                                                    </div>
                                                                    <div className="col-9">
                                                                        <Select
                                                                            required
                                                                            styles={styleSelect}
                                                                            menuPosition="fixed"
                                                                            isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                            name="invoice approval functions"
                                                                            value={
                                                                                functions
                                                                                    .filter((a) => a.name === action.value?.function)
                                                                                    .map(functionMap) ?? null
                                                                            }
                                                                            options={functions.map(functionMap)}
                                                                            placeholder={data.t.read("function")}
                                                                            classNamePrefix="select"
                                                                            onChange={(choice) =>
                                                                                updateInvoiceApprovalValue(action, {function: choice.value})
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                {action.type === REQUEST_SECTION_APPROVAL && (
                                                    <div className="border rounded p-2 col-12">
                                                        <div className="row mb-2">
                                                            <div className="col-3 align-self-center">
                                                                {data.t.read("rule.section_approval_target")}
                                                            </div>
                                                            <div className="col-9">
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    required
                                                                    styles={actionSelect}
                                                                    name="section approval target"
                                                                    classNamePrefix="select"
                                                                    options={sectionApprovalTargetOptions}
                                                                    value={sectionApprovalTargetValue(action.value?.target)}
                                                                    onChange={choice =>
                                                                        updateSectionApprovalValue(action, {target: choice.value})}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="row mb-2">
                                                            <div className="col-3 align-self-center">
                                                                {data.t.read("client_table.role")}
                                                            </div>
                                                            <div className="col-9">
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    required
                                                                    styles={actionSelect}
                                                                    name="section approval role"
                                                                    classNamePrefix="select"
                                                                    options={sectionApprovalRoleOptions}
                                                                    value={sectionApprovalRoleValue(action.value?.role)}
                                                                    placeholder={data.t.read("client_table.role").toLowerCase()}
                                                                    onChange={choice =>
                                                                        updateSectionApprovalValue(action, {role: choice?.value ?? ""})}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="row mb-2">
                                                            <div className="col-3 align-self-center">
                                                                {data.t.read("rule.section")}
                                                            </div>
                                                            <div className="col-9">
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    required
                                                                    styles={actionSelect}
                                                                    name="section approval section"
                                                                    classNamePrefix="select"
                                                                    options={codeOptionsFor("Section")}
                                                                    formatOptionLabel={option =>
                                                                        glAccountClassNode(option.value, option.description)}
                                                                    value={codeSelectValue("Section", action.value?.section)[0] ?? null}
                                                                    placeholder={data.t.read("rule.section")}
                                                                    noOptionsMessage={() => data.t.read("no_results_found")}
                                                                    onChange={choice =>
                                                                        updateSectionApprovalValue(action, {section: choice?.value ?? ""})}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {action.type === REQUEST_INVOICE_ANY_OF_APPROVAL && (
                                                    <div className="border rounded p-2 col-12">
                                                        <div className="row mb-2">
                                                            <div className="col-12 text-muted small">
                                                                {data.t.read("rule.request_invoice_any_of_approval_help")}
                                                            </div>
                                                        </div>
                                                        {anyOfSpecs(action).map((spec, specIdx) => (
                                                            <div key={specIdx} className="border rounded p-2 mb-2">
                                                                <div className="row mb-2">
                                                                    <div className="col-3 align-self-center">
                                                                        {data.t.read("approver")} #{specIdx + 1}
                                                                    </div>
                                                                    <div className="col-8">
                                                                        <Select
                                                                            menuPortalTarget={document.body}
                                                                            required
                                                                            styles={actionSelect}
                                                                            isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                            name={`any-of source ${specIdx}`}
                                                                            aria-label={`${data.t.read("approver")} #${specIdx + 1} — ${data.t.read("rule.request_invoice_any_of_approval")}`}
                                                                            value={invoiceApprovalSourceOptions.find((option) => option.value === spec.approver_source) || invoiceApprovalSourceOptions[0]}
                                                                            options={invoiceApprovalSourceOptions}
                                                                            placeholder={data.t.read("approver")}
                                                                            classNamePrefix="select"
                                                                            onChange={(choice) => updateAnyOfSpec(action, specIdx, {
                                                                                approver_source: choice.value,
                                                                                user_id: "",
                                                                                department: "",
                                                                                function: "",
                                                                            })}
                                                                        />
                                                                    </div>
                                                                    <div className="col-1 align-self-center text-center">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link p-0 text-danger"
                                                                            aria-label={`${data.t.read("delete")} ${data.t.read("approver")} #${specIdx + 1}`}
                                                                            disabled={!Utils.userHasPageAccess(data, "approval")}
                                                                            onClick={() => removeAnyOfSpec(action, specIdx)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} />
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {spec.approver_source === "manual_user" && (
                                                                    <div className="row mb-2">
                                                                        <div className="col-3 align-self-center">
                                                                            {data.t.read("user")}
                                                                        </div>
                                                                        <div className="col-9">
                                                                            <Select
                                                                                menuPortalTarget={document.body}
                                                                                required
                                                                                styles={actionSelect}
                                                                                isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                                name={`any-of user ${specIdx}`}
                                                                                aria-label={`${data.t.read("user")} — ${data.t.read("approver")} #${specIdx + 1}`}
                                                                                value={approvers.filter((a) => a.id_user === spec.user_id).map(approverMap) ?? null}
                                                                                options={approvers.map(approverMap)}
                                                                                placeholder={data.t.read("approver")}
                                                                                classNamePrefix="select"
                                                                                onChange={(choice) => updateAnyOfSpec(action, specIdx, {user_id: choice.value})}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {spec.approver_source === "department" && (
                                                                    <>
                                                                        <div className="row mb-2">
                                                                            <div className="col-3 align-self-center">
                                                                                {data.t.read("department")}
                                                                            </div>
                                                                            <div className="col-9">
                                                                                <Select
                                                                                    menuPortalTarget={document.body}
                                                                                    required
                                                                                    styles={styleSelect}
                                                                                    isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                                    name={`any-of dept ${specIdx}`}
                                                                                    aria-label={`${data.t.read("department")} — ${data.t.read("approver")} #${specIdx + 1}`}
                                                                                    value={departments.filter((a) => a._id?.$oid === spec.department).map(departmentMap) ?? null}
                                                                                    options={departments.map(departmentMap)}
                                                                                    placeholder={data.t.read("department")}
                                                                                    classNamePrefix="select"
                                                                                    onChange={(choice) => updateAnyOfSpec(action, specIdx, {department: choice.value})}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="row mb-2">
                                                                            <div className="col-3 align-self-center">
                                                                                {data.t.read("function")}
                                                                            </div>
                                                                            <div className="col-9">
                                                                                <Select
                                                                                    menuPortalTarget={document.body}
                                                                                    required
                                                                                    styles={styleSelect}
                                                                                    isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                                    name={`any-of function ${specIdx}`}
                                                                                    aria-label={`${data.t.read("function")} — ${data.t.read("approver")} #${specIdx + 1}`}
                                                                                    value={functions.filter((a) => a.name === spec.function).map(functionMap) ?? null}
                                                                                    options={functions.map(functionMap)}
                                                                                    placeholder={data.t.read("function")}
                                                                                    classNamePrefix="select"
                                                                                    onChange={(choice) => updateAnyOfSpec(action, specIdx, {function: choice.value})}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        ))}
                                                        <div className="row">
                                                            <div className="col-12">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0"
                                                                    disabled={!Utils.userHasPageAccess(data, "approval")}
                                                                    onClick={() => addAnyOfSpec(action)}
                                                                >
                                                                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                                                                    {data.t.read("rule.add_any_of_approver")}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {action.type === REQUEST_LINE_ITEM_APPROVAL && (
                                                    <div className="border rounded p-2 col-12">
                                                        <div className="row mb-2">
                                                            <div className="col-3 align-self-center">
                                                                {data.t.read("approver")}
                                                            </div>
                                                            <div className="col-9">
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    required
                                                                    styles={actionSelect}
                                                                    isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                    name="line item approval source"
                                                                    value={lineItemApprovalSourceOptions.find((option) => option.value === action.value?.approver_source) || lineItemApprovalSourceOptions[0]}
                                                                    options={lineItemApprovalSourceOptions}
                                                                    placeholder={data.t.read("approver")}
                                                                    classNamePrefix="select"
                                                                    onChange={(choice) =>
                                                                        updateLineItemApprovalValue(action, {
                                                                            approver_source: choice.value,
                                                                            user_id: "",
                                                                            department: "",
                                                                            function: "",
                                                                            role: "",
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* XPEN-3586: optional -- no role keeps every active approver of the sub-category. */}
                                                        {action.value?.approver_source === "subcategory_approvers" && (
                                                            <div className="row mb-2">
                                                                <div className="col-3 align-self-center">
                                                                    {data.t.read("client_table.role")}
                                                                </div>
                                                                <div className="col-9">
                                                                    <Select
                                                                        menuPortalTarget={document.body}
                                                                        isClearable
                                                                        styles={actionSelect}
                                                                        isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                        name="line item approval role"
                                                                        classNamePrefix="select"
                                                                        options={lineItemApprovalRoleOptions}
                                                                        value={lineItemApprovalRoleValue(action.value?.role)}
                                                                        placeholder={data.t.read("rule.any_role")}
                                                                        onChange={(choice) =>
                                                                            updateLineItemApprovalValue(action, {role: choice?.value ?? ""})
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}

                                                        {action.value?.approver_source === "manual_user" && (
                                                            <div className="row mb-2">
                                                                <div className="col-3 align-self-center">
                                                                    {data.t.read("user")}
                                                                </div>
                                                                <div className="col-9">
                                                                    <Select
                                                                        menuPortalTarget={document.body}
                                                                        required
                                                                        styles={actionSelect}
                                                                        isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                        name="line item approval user"
                                                                        value={
                                                                            approvers
                                                                                .filter((a) => a.id_user === action.value?.user_id)
                                                                                .map(approverMap) ?? null
                                                                        }
                                                                        options={approvers.map(approverMap)}
                                                                        placeholder={data.t.read("approver")}
                                                                        classNamePrefix="select"
                                                                        onChange={(choice) =>
                                                                            updateLineItemApprovalValue(action, {user_id: choice.value})
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}

                                                        {action.value?.approver_source === "department" && (
                                                            <>
                                                                <div className={"row mb-2"}>
                                                                    <div className="col-3 align-self-center">
                                                                        {data.t.read("department")}
                                                                    </div>
                                                                    <div className="col-9">
                                                                        <Select
                                                                            required
                                                                            styles={styleSelect}
                                                                            isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                            name="line item approval departments"
                                                                            value={
                                                                                departments
                                                                                    .filter((a) => a._id?.$oid === action.value?.department)
                                                                                    .map(departmentMap) ?? null
                                                                            }
                                                                            options={departments.map(departmentMap)}
                                                                            placeholder={data.t.read("department")}
                                                                            classNamePrefix="select"
                                                                            onChange={(choice) =>
                                                                                updateLineItemApprovalValue(action, {department: choice.value})
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className={"row mb-2"}>
                                                                    <div className="col-3 align-self-center">
                                                                        {data.t.read("function")}
                                                                    </div>
                                                                    <div className="col-9">
                                                                        <Select
                                                                            required
                                                                            styles={styleSelect}
                                                                            menuPosition="fixed"
                                                                            isDisabled={!Utils.userHasPageAccess(data, "approval")}
                                                                            name="line item approval functions"
                                                                            value={
                                                                                functions
                                                                                    .filter((a) => a.name === action.value?.function)
                                                                                    .map(functionMap) ?? null
                                                                            }
                                                                            options={functions.map(functionMap)}
                                                                            placeholder={data.t.read("function")}
                                                                            classNamePrefix="select"
                                                                            onChange={(choice) =>
                                                                                updateLineItemApprovalValue(action, {function: choice.value})
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                {action.type === "exclude_approver_roles" && (
                                                    <div className="col-12">
                                                        <Select
                                                            menuPortalTarget={document.body}
                                                            styles={actionSelect}
                                                            isMulti
                                                            isClearable
                                                            required
                                                            classNamePrefix="select"
                                                            name="exclude-approver-roles-select"
                                                            placeholder={data.t.read("client_table.role").toLowerCase()}
                                                            options={(data.config?.roles || []).map((r) => ({
                                                                value: r,
                                                                label: data.t.read("role_" + r.toLowerCase()),
                                                            }))}
                                                            value={(Array.isArray(action.value) ? action.value : []).map((r) => ({
                                                                value: r,
                                                                label: data.t.read("role_" + String(r).toLowerCase()),
                                                            }))}
                                                            onChange={(selected) =>
                                                                setActionValue(action, (selected || []).map((s) => s.value).filter(Boolean))
                                                            }
                                                        />
                                                    </div>
                                                )}
                                                {action.type === "append_approver_list" && (
                                                    <div className="col-12">
                                                        <Select
                                                            menuPortalTarget={document.body}
                                                            required
                                                            styles={actionSelect}
                                                            isDisabled={
                                                                action.type === "append_approver_list" &&
                                                                !Utils.userHasPageAccess(data, "approval")
                                                            }
                                                            name="approvers"
                                                            value = {
                                                                approverOptions.find(a =>
                                                                    (action.value.kind === "matter_responsible" && a.value.kind === "matter_responsible") ||
                                                                    (action.value.kind === "gl_responsible" && a.value.kind === "gl_responsible") ||
                                                                    (action.value.kind === "user" && action.value.id === a.value.id)
                                                                )
                                                            }
                                                            options={approverOptions}
                                                            placeholder={data.t.read("approver")}
                                                            classNamePrefix="select"
                                                            onChange={(choice) =>
                                                                updateAction("value", action, {
                                                                    target: {value: choice.value},
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                )}

                                                {action.type === "change_status" && (
                                                    <div className="col-12">
                                                        <Select
                                                            required
                                                            styles={actionSelect}
                                                            name="invoice status select"
                                                            value={{
                                                                value: action.value,
                                                                label: data.t.read("status." + (action.value || "").toLowerCase()),
                                                            }}
                                                            options={data.config.invoice_status.map((tmp) => ({
                                                                value: tmp,
                                                                label: data.t.read("status." + tmp.toLowerCase()),
                                                            }))}
                                                            placeholder={data.t.read("rule.status")}
                                                            classNamePrefix="select"
                                                            onChange={(choice) =>
                                                                updateAction("value", action, {
                                                                    target: {value: choice.value},
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                )}

                                                {action.type === "change_due_date" && (
                                                    <div className="col-12">
                                                        <Select
                                                            required
                                                            styles={actionSelect}
                                                            name="due date action select"
                                                            value={action.value ? {
                                                                value: action.value,
                                                                label: data.t.read(`rule.${action.value}`),
                                                            } : null}
                                                            options={[
                                                                { value: "in_30_days", label: data.t.read("rule.in_30_days") },
                                                                { value: "in_45_days", label: data.t.read("rule.in_45_days") },
                                                                { value: "on_receipt", label: data.t.read("rule.on_receipt") },
                                                                { value: "on_day_next_month", label: data.t.read("rule.on_day_next_month") },
                                                            ]}
                                                            placeholder={data.t.read("invoice.due_date")}
                                                            classNamePrefix="select"
                                                            onChange={(choice) =>
                                                                setLocalStep({
                                                                    ...localStep,
                                                                    [actionsKey]: localStep[actionsKey].map((a) =>
                                                                        a.id === action.id
                                                                            ? {
                                                                                ...a,
                                                                                value: choice?.value ?? "",
                                                                                day: (choice?.value ?? "") === "on_day_next_month"
                                                                                    ? (a.day || "")
                                                                                    : "",
                                                                            }
                                                                            : a
                                                                    ),
                                                                })
                                                            }
                                                        />
                                                        {action.value === "on_day_next_month" && (
                                                            <div className="mt-2">
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max="31"
                                                                    className="form-control workflow-edit-step-modal__control"
                                                                    placeholder={data.t.read("rule.day_of_next_month")}
                                                                    value={action.day ?? ""}
                                                                    onChange={(e) =>
                                                                        updateAction("day", action, e)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {action.type === "change_item_detail" && (
                                                    <div className="d-flex col-12 align-items-center mt-1">
                                                        <div className="col-4">
                                                            <Select
                                                                styles={actionSelect}
                                                                name="field select"
                                                                value={lineItemDetailFieldOptions.find(option => option.value === action.field) ?? ""}
                                                                options={lineItemDetailFieldOptions}
                                                                classNamePrefix="select"
                                                                onChange={(choice) =>
                                                                    updateAction("field", action, {
                                                                        target: {value: choice.value},
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                        {renderFieldValueInput(action)}
                                                    </div>
                                                )}
                                                {action.type === "change_invoice_default_field_value" && (
                                                    <div className="d-flex col-12 align-items-center mt-1">
                                                        <div className="col-4">
                                                            <Select
                                                                styles={actionSelect}
                                                                name="field select"
                                                                value={itemDetailFieldOptions.find(option => option.value === action.field) ?? ""}
                                                                options={itemDetailFieldOptions.filter(option => option.value !== "Description")}
                                                                classNamePrefix="select"
                                                                onChange={(choice) =>
                                                                    updateAction("field", action, {
                                                                        target: {value: choice.value},
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                        {renderFieldValueInput(action)}
                                                    </div>
                                                )}
                                                {action.type === "send_email" && (
                                                    <div className="col-12 row align-items-center">
                                                        <div className="pl-3 col-6">
                                                            <input
                                                                // type="email"
                                                                autoComplete="new-password"
                                                                
                                                                required
                                                                className="form-control workflow-edit-step-modal__control"
                                                                placeholder={data.t.read("rule.email")}
                                                                value={action.value}
                                                                onChange={(e) =>
                                                                    updateAction("value", action, e)
                                                                }
                                                            />
                                                        </div>
                                                        <label className="font-xsmall ml-1">
                                                            Ex: me@my-company.com
                                                        </label>
                                                    </div>
                                                )}

                                                {action.type === "change_tax_codes" && (
                                                    <div className="col-12">
                                                        <Select
                                                            menuPortalTarget={document.body}
                                                            styles={actionSelect}
                                                            name="tax code select"
                                                            value={taxCodeOptions.find((option) => option.value === action.value) ?? null}
                                                            options={taxCodeOptions}
                                                            placeholder={data.t.read("rule.tax_code")}
                                                            classNamePrefix="select"
                                                            onChange={(choice) =>
                                                                updateAction("value", action, {
                                                                target: { value: choice?.value ?? "" },
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                )}

                                                {action.type === "change_item_tax_code" && (
                                                    <div className="col-12">
                                                        <Select
                                                            menuPortalTarget={document.body}
                                                            styles={actionSelect}
                                                            name="tax code select"
                                                            value={taxCodeOptions.find((option) => option.value === action.value) ?? null}
                                                            options={taxCodeOptions}
                                                            placeholder={data.t.read("rule.tax_code")}
                                                            classNamePrefix="select"
                                                            onChange={(choice) =>
                                                                updateAction("value", action, {
                                                                    target: { value: choice?.value ?? "" },
                                                                })
                                                            }
                                                        />
                                                    </div>
                                                )}

                                                {action.type === "change_unit" && (
                                                    <div className="col-12 row">
                                                        <div className="col-4">
                                                        <FormTextfield format={"data"}
                                                                       value={action.value}
                                                                       locked={null}
                                                                       validateChange={val => updateAction("value", action, {target: {value: val}})}
                                                                       searchURL="/suppliers/unit/search/"
                                                                       hideAddOperation={true}
                                                                       hideNoResults={true}
                                                                       classes={"workflow-edit-step-modal__control p-2 border"}/>
                                                    </div>
                                                    </div>
                                                )}

                                                {action.type === "change_currency" && (
                                                    <div className="col-12 row">
                                                        <div className="col-4">
                                                            <input
                                                                type="text"
                                                                required
                                                                className="form-control workflow-edit-step-modal__control"
                                                                placeholder={data.t.read("rule.currency")}
                                                                value={action.value}
                                                                onChange={(e) =>
                                                                    updateAction("value", action, e)
                                                                }
                                                            />
                                                        </div>
                                                        <label className="col-8 font-xsmall">
                                                            Ex: CAD
                                                        </label>
                                                    </div>
                                                )}

                                                {action.type === "split_mileage_by_secondary_rate" && (() => {
                                                    // XPEN-3427: an open_amount type has no rate/distance, so an
                                                    // absorption rule bound to it is meaningless — the server
                                                    // rejects it outright; this keeps the admin from being offered
                                                    // a choice that will always be refused.
                                                    const eligibleTravelTypes = (expenseRates?.mileage_travel_types ?? []).filter(t => t.active && t.travel_mode !== 'open_amount');
                                                    const selectedTT = eligibleTravelTypes.find(t => t.id === action.value?.travel_type_id);
                                                    return (
                                                    <div className="col-12">
                                                        <div className="row align-items-end mb-2">
                                                            <div className="col-12">
                                                                <label className="small mb-1">
                                                                    {data.t.read("mileage_travel_type.label") || "Travel Type"}
                                                                </label>
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    styles={actionSelect}
                                                                    name="mileage travel type select"
                                                                    value={selectedTT ? { value: selectedTT.id, label: selectedTT.name } : null}
                                                                    options={eligibleTravelTypes.map(t => ({ value: t.id, label: t.name }))}
                                                                    placeholder={data.t.read("mileage_travel_type.label") || "Travel Type"}
                                                                    classNamePrefix="select"
                                                                    onChange={(choice) => updateMileageSplitValue(action, {travel_type_id: choice?.value ?? ""})}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="row align-items-end">
                                                            <div className="col-4">
                                                                <label className="small mb-1">
                                                                    {data.t.read("rule.secondary_mileage_rate")}
                                                                </label>
                                                                <MoneyInput
                                                                    className="form-control workflow-edit-step-modal__control"
                                                                    value={action.value?.secondary_rate ?? ""}
                                                                    onChange={(value) => updateMileageSplitValue(action, {secondary_rate: value})}
                                                                    locale={`${data.t.lang}-${data.t.lang.toUpperCase()}`}
                                                                    placeholder={data.t.read("rule.secondary_mileage_rate")}
                                                                />
                                                            </div>
                                                            <div className="col-5">
                                                                <label className="small mb-1">
                                                                    {data.t.read("rule.billable_disbursement_code")}
                                                                </label>
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    styles={actionSelect}
                                                                    name="billable disbursement code select"
                                                                    value={costTypeOptions.find(option => option.value === action.value?.billable_cost_type) ?? null}
                                                                    options={costTypeOptions}
                                                                    placeholder={data.t.read("rule.billable_disbursement_code")}
                                                                    classNamePrefix="select"
                                                                    onChange={(choice) => updateMileageSplitValue(action, {billable_cost_type: choice?.value ?? ""})}
                                                                />
                                                            </div>
                                                            <div className="col-3 small text-muted">
                                                                {data.t.read("rule.standard_mileage_rate")}: {selectedTT?.rate ?? "-"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    );
                                                })()}

                                                {action.type === "split_tip_into_separate_item" && (
                                                    <div className="col-12 row align-items-end">
                                                        <div className="col-4">
                                                            <label className="small mb-1">
                                                                {data.t.read("rule.tip_disbursement_code")}
                                                            </label>
                                                            <AutoCompleteField
                                                                data={data}
                                                                name="tip disbursement code select"
                                                                value={action.value?.cost_type ?? ""}
                                                                searchUrl="/cost_type/search/"
                                                                className="form-control"
                                                                minLength={1}
                                                                placeholder={data.t.read("rule.tip_disbursement_code")}
                                                                onChange={(val) => updateTipSplitValue(action, {cost_type: val?.name ?? val ?? ""})}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {action.type === "set_invoice_number" && <InvoiceNumberTemplate data={data} value={action.value} onChange={value => updateAction("value", action, {target: {value}})} />}

                                                {action.type === RENAME_INVOICE_FILE && (() => {
                                                    const value = {
                                                        ...DEFAULT_RENAME_FILE_VALUE,
                                                        ...(typeof action.value === "object" && action.value !== null ? action.value : {}),
                                                    };
                                                    const pieces = value.pieces || [];
                                                    return (
                                                        <div className="col-12">
                                                            <div className="row align-items-end">
                                                                {[0, 1, 2].map(index => (
                                                                    <div className="col-3" key={`rename-file-piece-${index}`}>
                                                                        <label className="small mb-1">
                                                                            {data.t.read("rule.filename_piece")} {index + 1}
                                                                        </label>
                                                                        <Select
                                                                            menuPortalTarget={document.body}
                                                                            styles={actionSelect}
                                                                            name={`rename file piece ${index + 1}`}
                                                                            value={renameFilePieceOptions.find(option => option.value === (pieces[index] || "")) ?? renameFilePieceOptions[0]}
                                                                            options={renameFilePieceOptions}
                                                                            classNamePrefix="select"
                                                                            onChange={(choice) => updateRenameFilePiece(action, index, choice?.value ?? "")}
                                                                        />
                                                                    </div>
                                                                ))}
                                                                <div className="col-3">
                                                                    <label className="small mb-1">
                                                                        {data.t.read("rule.filename_separator")}
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control workflow-edit-step-modal__control"
                                                                        value={value.separator ?? "_"}
                                                                        maxLength={5}
                                                                        onChange={(e) => updateRenameFileValue(action, {separator: e.target.value})}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}

                                                {action.type === "split_items" && (
                                                    <div className="col-12">
                                                        {Array.isArray(action.value) &&
                                                            action.value.map((line) => (
                                                                <div
                                                                    key={"l-" + line.id}
                                                                    className="d-flex mb-2 p-1 flex-wrap"
                                                                >
                                                                    <div>
                                                                        {action.value.length > 1 && (
                                                                            <FontAwesomeIcon
                                                                                className="mr-2 mt-1"
                                                                                icon={faMinusSquare}
                                                                                type="button"
                                                                                color="red"
                                                                                onClick={() => removeSplitItemValue(action, line)}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div className="row border p-1 col">
                                                                        <div className="col-12">
                                                                            <div className="row mb-1">
                                                                                <div className="col-3 font-weight-bold">
                                                                                    {line.id === 1
                                                                                        ? data.t.read("rule.a_line_containing")
                                                                                        : data.t.read("rule.and_add_line")}
                                                                                </div>
                                                                                <div className="col-3">
                                                                                    <div
                                                                                        className="input-group input-group-sm">
                                                                                        <div
                                                                                            className="input-group-prepend">
                                                                                        <span
                                                                                            className="input-group-text">%</span>
                                                                                        </div>
                                                                                        <input
                                                                                            type="number"
                                                                                            min="-100"
                                                                                            max="1000"
                                                                                            value={line.percent}
                                                                                            onChange={(e) =>
                                                                                                updateSplitItemValue(action, line, "percent", e)
                                                                                            }
                                                                                            style={{maxWidth: "100%"}}
                                                                                            className="form-control workflow-edit-step-modal__control"
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                                <div className="col-3">
                                                                                    {data.t.read("rule.of_amount")},
                                                                                </div>
                                                                            </div>

                                                                            <div className="row mb-1">
                                                                                <div className="col-3">
                                                                                    {data.t.read("rule.up_to_max_of")}
                                                                                </div>
                                                                                <div className="col-3">
                                                                                    <div
                                                                                        className="input-group input-group-sm">
                                                                                        <div
                                                                                            className="input-group-prepend">
                                                                                        <span
                                                                                            className="input-group-text">$</span>
                                                                                        </div>
                                                                                        <input
                                                                                            type="number"
                                                                                            value={line.maxAmount}
                                                                                            style={{maxWidth: "100%"}}
                                                                                            onChange={(e) =>
                                                                                                updateSplitItemValue(
                                                                                                    action,
                                                                                                    line,
                                                                                                    "maxAmount",
                                                                                                    e
                                                                                                )
                                                                                            }
                                                                                            className="form-control workflow-edit-step-modal__control"
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                                <div className="col-5">
                                                                                    ({data.t.read("rule.leave_empty_for_unlimited")})
                                                                                </div>
                                                                            </div>

                                                                            <div className="row mb-1">
                                                                                <div className="col-3">
                                                                                    {data.t.read("rule.and_replace")}
                                                                                </div>
                                                                                <div className="col-3">
                                                                                    <input
                                                                                        type="number"
                                                                                        min="-100"
                                                                                        max="1000"
                                                                                        value={line.updateCharacterAmount}
                                                                                        onChange={(e) =>
                                                                                            updateSplitItemValue(
                                                                                                action,
                                                                                                line,
                                                                                                "updateCharacterAmount",
                                                                                                e
                                                                                            )
                                                                                        }
                                                                                        style={{maxWidth: "100%"}}
                                                                                            className="form-control workflow-edit-step-modal__control"
                                                                                    />
                                                                                </div>
                                                                                <div className="col-3">
                                                                                    {data.t.read("rule.character_at")}
                                                                                </div>
                                                                                <div className="col-3">
                                                                                    <Select
                                                                                        styles={styleSelect}
                                                                                        name="replace at the start select"
                                                                                        value={{
                                                                                            value: line.replaceAtTheStart,
                                                                                            label: data.t.read(
                                                                                                line.replaceAtTheStart
                                                                                                    ? "rule.the_start"
                                                                                                    : "rule.the_end"
                                                                                            ),
                                                                                        }}
                                                                                        options={[
                                                                                            {
                                                                                                value: false,
                                                                                                label: data.t.read("rule.the_end"),
                                                                                            },
                                                                                            {
                                                                                                value: true,
                                                                                                label: data.t.read("rule.the_start"),
                                                                                            },
                                                                                        ]}
                                                                                        classNamePrefix="select"
                                                                                        onChange={(choice) =>
                                                                                            updateSplitItemValue(
                                                                                                action,
                                                                                                line,
                                                                                                "replaceAtTheStart",
                                                                                                {
                                                                                                    target: {value: choice.value},
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </div>

                                                                            <div className="row mb-1">
                                                                                <div className="col-3">
                                                                                    {data.t.read("rule.of")}
                                                                                </div>
                                                                                <div className="col-3">
                                                                                    <Select
                                                                                        styles={styleSelect}
                                                                                        name="update info select"
                                                                                        value={{
                                                                                            value: line.updateInfo || "",
                                                                                            label: line.updateInfo || "--",
                                                                                        }}
                                                                                        options={[
                                                                                            {value: "", label: "--"},
                                                                                            {
                                                                                                value: "Office",
                                                                                                label: "Office"
                                                                                            },
                                                                                            ...(Utils.isExportFormat(data, "elite") ? [{
                                                                                                value: "GLOffice",
                                                                                                label: "GLOffice"
                                                                                            }] : []),
                                                                                            {
                                                                                                value: "GLNatural",
                                                                                                label: "GLNatural"
                                                                                            },
                                                                                            {
                                                                                                value: "CostType",
                                                                                                label: "CostType"
                                                                                            },
                                                                                        ]}
                                                                                        classNamePrefix="select"
                                                                                        onChange={(choice) =>
                                                                                            updateSplitItemValue(
                                                                                                action,
                                                                                                line,
                                                                                                "updateInfo",
                                                                                                {
                                                                                                    target: {value: choice.value},
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                {line.updateInfo && line.updateInfo.length > 0 && (
                                                                                    <>
                                                                                        <div className="col-3">
                                                                                            {data.t.read("rule.by")}
                                                                                        </div>
                                                                                        <div className="col-3">
                                                                                            <input
                                                                                                type="text"
                                                                                                required
                                                                                                className="form-control form-control-sm workflow-edit-step-modal__control"
                                                                                                placeholder="Value"
                                                                                                value={line.updateInfoValue}
                                                                                                onChange={(e) =>
                                                                                                    updateSplitItemValue(
                                                                                                        action,
                                                                                                        line,
                                                                                                        "updateInfoValue",
                                                                                                        e
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}

                                                        <div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-success"
                                                                onClick={() => addSplitItemValue(action)}
                                                            >
                                                                + {data.t.read("rule.line")}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {action.type === "change_all_totals" && (
                                                    <div className="input-group-prepend ml-3">
                                                        <input
                                                            type="number"
                                                            min="-100"
                                                            max="100"
                                                            required
                                                            value={action.value}
                                                            onChange={(e) => updateAction("value", action, e)}
                                                            style={{maxWidth: "100%"}}
                                                            className="form-control workflow-edit-step-modal__control"
                                                        />
                                                        <span className="input-group-text">%</span>
                                                    </div>
                                                )}
                                                {action.type === "tax_deduction" && (
                                                    <div className="input-group-prepend ml-3">
                                                        <input
                                                            type="number"
                                                            min="-100"
                                                            max="100"
                                                            required
                                                            value={action.value}
                                                            onChange={(e) => updateAction("value", action, e)}
                                                            style={{maxWidth: "100%"}}
                                                            className="form-control workflow-edit-step-modal__control"
                                                        />
                                                        <span className="input-group-text">%</span>
                                                    </div>
                                                )}

                                                {action.type === "change_user" && (
                                                    <div className="input-group-prepend col-12">
                                                        <input
                                                            type="text"
                                                            value={action.value}
                                                            className="form-control workflow-edit-step-modal__control"
                                                            placeholder={data.t.read("user")}
                                                            onChange={(e) => updateAction("value", action, e)}
                                                        />
                                                    </div>
                                                )}

                                                {action.type === "change_item_description" && (
                                                    <div className="col-12">
                                                        <input
                                                            type="text"
                                                            value={action.value}
                                                            className="form-control workflow-edit-step-modal__control"
                                                            placeholder="Description"
                                                            onChange={(e) => updateAction("value", action, e)}
                                                        />
                                                        <div className="d-flex mt-2 align-items-center">
                                                            <label className="mr-3 mb-0">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={Boolean(action.include_supplier_name)}
                                                                    onChange={(e) =>
                                                                        updateAction("include_supplier_name", action, {
                                                                            target: { value: e.target.checked },
                                                                        })
                                                                    }
                                                                />
                                                                <span className="ml-1">{data.t.read("rule.add_supplier_name")}</span>
                                                            </label>
                                                            <label className="mb-0">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={Boolean(action.include_invoice_number)}
                                                                    onChange={(e) =>
                                                                        updateAction("include_invoice_number", action, {
                                                                            target: { value: e.target.checked },
                                                                        })
                                                                    }
                                                                />
                                                                <span className="ml-1">{data.t.read("rule.add_invoice_number")}</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                )}
                                                {action.type === "change_cheque_reference" && (
                                                    <div className="col-12">
                                                        <input
                                                            type="text"
                                                            value={action.value}
                                                            required
                                                            className="form-control"
                                                            placeholder={data.t.read("rule.change_cheque_reference")}
                                                            onChange={(e) => updateAction("value", action, e)}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            )}

                                        </div>
                                        {/* Action remove icon */}
                                        <div className="workflow-edit-step-modal__card-remove">
                                            {/* validateStep requires one action on the primary side; the
                                                alternate side is optional and must be emptiable. */}
                                            {(side === ALTERNATE_SIDE
                                              || (localStep[actionsKey] || []).length > 1) && (
                                                <FontAwesomeIcon
                                                    className="text-muted"
                                                    data-testid={`remove-action-${action.id}`}
                                                    icon={faTrash}
                                                    type="button"
                                                    onClick={() => removeAction(action)}
                                                />
                                            )}
                                        </div>

                                    </div>

                                </div>
                            ))}
                </div>
            </div>
            )}
            <div className="workflow-edit-step-modal__footer">
                <button type="button" className="btn btn-outline-dark" onClick={onCancel}>
                    {data.t.read("cancel")}
                </button>
                <button type="submit" className="btn btn-primary">
                    {data.t.read("save")}
                </button>
            </div>
        </form>
    );
};

export default observer(WorkflowEditStepModal);
